/**
 * ============================================================================
 * THE SHOWROOM v3.0 - MAIN JAVASCRIPT
 * Tea & Spice Shoppe - E-commerce Filter System
 * ============================================================================
 * 
 * TABLE OF CONTENTS
 * -----------------
 * 1. STATE & CONFIGURATION .............. Line ~30
 *    - DOM references, state variables, filter objects
 * 
 * 2. SHIPPING ZONE SYSTEM ............... Line ~70
 *    - Load/save shipping zones, display updates, zone selection
 * 
 * 3. BACK-IN-STOCK NOTIFICATIONS ........ Line ~170
 *    - Inline notify form handling, Cloudflare Worker integration
 * 
 * 4. URL STATE MANAGEMENT ............... Line ~230
 *    - Save/restore filters to URL, deep linking support
 * 
 * 5. CART SYSTEM ........................ Line ~320
 *    - Fetch cart, update quantities, badges, line totals
 * 
 * 6. RECIPE CARD SYSTEM ................. Line ~420
 *    - Recipe data loading, spice tracking, cookbook mode
 *    - Add/remove recipe spices, optional spices, unlock counts
 * 
 * 7. SPICE & RECIPE FILTER SYSTEM ....... Line ~900
 *    - Four mutually exclusive dimensions: Type, Protein, Meals, Cuisine
 *    - Track switching, filter state management
 * 
 * 8. CORE FILTER SYSTEM ................. Line ~1150
 *    - Tea/Herbal dimension tabs, sub-filter buttons
 *    - Filter pills, clear all, collection switching
 * 
 * 9. PRODUCT MATCHING LOGIC ............. Line ~1350
 *    - productMatchesFilters, hypothetical matching, filter counts
 * 
 * 10. DISPLAY & UI ...................... Line ~1550
 *     - showCollection, showCartView, carousels, scroll arrows
 * 
 * 11. INITIALIZATION .................... Line ~1750
 *     - Event binding, startup sequence
 * 
 * ============================================================================
 */

(function() {
  'use strict';

  // Carousel initialization guard — prevents stacking event listeners
  const __initializedCarousels = new WeakSet();

  // Deferred filter count calculation — cancellable idle callback
  let __pendingCountUpdate = null;

  // Track which collection templates have been inflated into the DOM
  const __inflatedCollections = new Set(['loose-leaf-tea-canada', 'new-here', 'shipping-zones', 'spice-recipes', 'departure-lounge']);

  /**
   * Inflate a deferred collection from its <template> tag into the product grid.
   * Runs once per collection — subsequent switches find cards already in the DOM.
   */
  function inflateCollection(collectionKey) {
    if (__inflatedCollections.has(collectionKey)) return;
    
    const tpl = document.getElementById('tpl-' + collectionKey);
    if (!tpl) return;
    
    const productGrid = document.getElementById('productGrid');
    
    // Move all cards from template into the grid
    productGrid.appendChild(document.importNode(tpl.content, true));
    
    // Remove the template tag (no longer needed)
    tpl.remove();
    
    // Set data-active-tab on newly inflated cards
    productGrid.querySelectorAll('.product-card:not([data-active-tab])').forEach(function(card) {
      var activePanel = card.querySelector('.card-tab-panel.active');
      if (activePanel && activePanel.dataset.panel) {
        card.dataset.activeTab = activePanel.dataset.panel;
      }
    });
    
    // Re-cache DOM references that were captured before this collection existed
    subFilterBtns = document.querySelectorAll('.sub-filter-btn');
    productCards = document.querySelectorAll('.product-card');
    
    // Re-initialize systems that bind to new DOM elements
    initToggleSelectors();
    initializeCarousels();
    
    // Re-populate pairing lists for newly inflated cards
    if (typeof window.populatePairingLists === 'function') {
      window.populatePairingLists();
    }
    
    __inflatedCollections.add(collectionKey);
  }

  window.inflateCollection = inflateCollection;

  /* ==========================================================================
     1. STATE & CONFIGURATION
     ========================================================================== */

  // DOM References
  const filterBtns = document.querySelectorAll('.category-btn');
  const subFilters = document.querySelectorAll('.sub-filters-enhanced');
  let subFilterBtns = document.querySelectorAll('.sub-filter-btn');
  let productCards = document.querySelectorAll('.product-card');
  const noResults = document.getElementById('noResults');
  const cartBadge = document.getElementById('cartBadgeAction');
  const cartTotal = document.getElementById('cartTotalAction');
  const emptyCartMessage = document.getElementById('emptyCartMessage');
  const successToast = document.getElementById('successToast');

  // Application State
  let activeCollection = 'loose-leaf-tea-canada';
  let isCartView = false;
  let shopifyCart = null;
  let activeFilters = {};
  let isProcessing = false;

  // Spice Filter State (v3.3 - simplified)
  let spiceFilters = {
    spice_type: null,
    spice_protein: null,
    spice_dish: null,
    spice_cuisine: null,
  };

  // Recipe Filter State (for standalone Recipes toggle)
  let recipeFilters = {
    spice_protein: null,
    spice_dish: null,
    spice_cuisine: null
  };

  // Spice Recipe Mode (when viewing recipes from a spice card)
  let spiceRecipeMode = {
    active: false,
    previousCollection: null,
    spiceName: '',
    spiceVariantId: ''
  };

  // Recipe State
  let recipeData = [];
  let currentCookbookSpices = null;

  // Shipping State
  let shippingZoneData = null;
  let selectedShippingZone = null;

  // New Here? Section State
  let activeNewHereSection = 'getting-started';

  // Cart Toggle Consistency
  let lastActiveVariantId = null;


  /* ==========================================================================
     2. SHIPPING ZONE SYSTEM
     ========================================================================== */

  /**
   * Load shipping zone data from embedded JSON
   */
  function loadShippingZoneData() {
    const dataElement = document.getElementById('shippingZoneData');
    if (dataElement) {
      try {
        shippingZoneData = JSON.parse(dataElement.textContent);
      } catch (e) {
        console.error('Failed to parse shipping zone data:', e);
      }
    }
  }

  /**
   * Initialize shipping zone from localStorage or default
   */
  function initializeShippingZone() {
    loadShippingZoneData();
    if (!shippingZoneData) return;
    
    const savedZoneHandle = localStorage.getItem('selectedShippingZone');
    if (savedZoneHandle) {
      selectedShippingZone = shippingZoneData.zones.find(z => z.handle === savedZoneHandle);
    }
    if (!selectedShippingZone) {
      selectedShippingZone = shippingZoneData.zones.find(z => z.isDefault) || shippingZoneData.zones[0];
    }
    
    updateShippingDisplay();
  }

  /**
   * Update shipping button and zone card visual states
   */
  function updateShippingDisplay() {
    if (!selectedShippingZone) return;
    
    // Update Shipping button: "$12 (AB)"
    const priceDisplay = document.getElementById('selectedZonePrice');
    if (priceDisplay) {
      const price = selectedShippingZone.price === 0 ? 'FREE' : `$${(selectedShippingZone.price / 100).toFixed(0)}`;
      const abbrev = getZoneAbbreviation(selectedShippingZone.label);
      priceDisplay.textContent = `${price} (${abbrev})`;
    }
    
    // Update zone cards visual state
    document.querySelectorAll('.product-card--shipping-zone').forEach(card => {
      const isSelected = card.dataset.zoneHandle === selectedShippingZone.handle;
      const header = card.querySelector('.product-card-header');
      const btn = card.querySelector('.select-zone-btn');
      const desc = card.querySelector('.product-description');
      
      if (isSelected) {
        header?.classList.add('zone-selected');
        if (btn) {
          btn.textContent = '✓ Selected';
          btn.style.background = '#1e8449';
        }
        if (desc && !desc.innerHTML.includes('Currently Selected')) {
          desc.innerHTML += '<br><br><em style="color: #1e8449;">✓ Currently Selected</em>';
        }
      } else {
        header?.classList.remove('zone-selected');
        if (btn) {
          btn.textContent = 'Select This Zone';
          btn.style.background = '#D97706';
        }
        if (desc) {
          desc.innerHTML = desc.innerHTML.replace(/<br><br><em[^>]*>✓ Currently Selected<\/em>/g, '');
        }
      }
    });
    
    updateCartDisplays();
  }

  /**
   * Get short abbreviation for zone label
   */
  function getZoneAbbreviation(label) {
    const abbrevs = {
      'Local Delivery (Calgary)': 'YYC',
      'Alberta': 'AB',
      'BC, SK, MB': 'WST',
      'ON & QC': 'ON/QC',
      'Atlantic Canada': 'ATL',
      'NWT & Yukon': 'NWT/YK',
      'Nunavut': 'NU'
    };
    return abbrevs[label] || label.substring(0, 3).toUpperCase();
  }

  /**
   * Select a shipping zone and save to localStorage
   */
  function selectShippingZone(zoneHandle) {
    if (!shippingZoneData) return;
    const zone = shippingZoneData.zones.find(z => z.handle === zoneHandle);
    if (zone) {
      selectedShippingZone = zone;
      localStorage.setItem('selectedShippingZone', zoneHandle);
      updateShippingDisplay();
      showToast(`Shipping: ${zone.label}`);
    }
  }

  /**
   * Update cart subtotal and estimated total displays
   */
  function updateCartDisplays() {
    if (!shopifyCart) return;
    
    const subtotal = shopifyCart.total_price || 0;
    const shippingCents = selectedShippingZone ? selectedShippingZone.price : 1200;
    const estimatedTotal = subtotal + shippingCents;
    const itemCount = shopifyCart.item_count || 0;
    
    // View Cart button - shows subtotal
    if (cartTotal) {
      if (itemCount > 0) {
        cartTotal.textContent = `$${(subtotal / 100).toFixed(2)}`;
        cartTotal.style.display = 'inline';
      } else {
        cartTotal.style.display = 'none';
      }
    }
    
    // Start the Car button - shows estimated total
    const estimatedDisplay = document.getElementById('estimatedTotalAction');
    if (estimatedDisplay) {
      if (itemCount > 0) {
        estimatedDisplay.textContent = `$${(estimatedTotal / 100).toFixed(2)}`;
        estimatedDisplay.style.display = 'inline';
      } else {
        estimatedDisplay.style.display = 'none';
      }
    }
  }


  /* ==========================================================================
     3. BACK-IN-STOCK NOTIFICATIONS
     ========================================================================== */

  /**
   * Initialize inline notify form handlers
   * Uses Cloudflare Worker for email capture
   */
  function initializeInlineNotify() {
    // Show notify form when button clicked
    document.addEventListener('click', function(e) {
      const notifyBtn = e.target.closest('.variant-notify-btn');
      if (notifyBtn) {
        e.stopPropagation();
        const variantId = notifyBtn.dataset.variantId;
        const productId = notifyBtn.dataset.productId;
        const variantTitle = notifyBtn.dataset.variantTitle;
        notifyBtn.style.display = 'none';
        const container = notifyBtn.closest('.variant-notify-container');
        container.setAttribute('data-state', 'form');
        container.dataset.capturedVariantId = variantId;
        container.dataset.capturedProductId = productId;
        container.dataset.capturedVariantTitle = variantTitle;
        const form = container.querySelector('.variant-notify-form');
        form.style.display = 'flex';
        setTimeout(() => form.querySelector('.variant-notify-email').focus(), 100);
      }
    });

    // Submit notify form
    document.addEventListener('click', async function(e) {
      const submitBtn = e.target.closest('.variant-notify-submit');
      if (submitBtn) {
        e.stopPropagation();
        const container = submitBtn.closest('.variant-notify-container');
        const emailInput = container.querySelector('.variant-notify-email');
        const email = emailInput.value.trim();
        if (!email || !email.includes('@')) { 
          emailInput.style.borderColor = '#e74c3c'; 
          emailInput.focus(); 
          return; 
        }
        const variantId = container.dataset.capturedVariantId;
        const productId = container.dataset.capturedProductId;
        const variantTitle = container.dataset.capturedVariantTitle;
        const card = container.closest('.product-card');
        const productTitle = card ? card.querySelector('.product-card-header-title').textContent : '';
        submitBtn.textContent = '⏳'; 
        submitBtn.disabled = true; 
        emailInput.disabled = true;
        try {
          const response = await fetch('https://tea-shop-restock-notifications.tiny-river-77dd.workers.dev', { 
            method: 'POST', 
            headers: { 'Content-Type': 'application/json' }, 
            body: JSON.stringify({ email, variantId, productId, productTitle, variantTitle }) 
          });
          if (!response.ok) throw new Error('Failed');
          const form = container.querySelector('.variant-notify-form');
          const success = container.querySelector('.variant-notify-success');
          container.setAttribute('data-state', 'success');
          form.style.display = 'none';
          success.style.display = 'block';
        } catch (error) { 
          console.error(error); 
          submitBtn.textContent = '🔔'; 
          submitBtn.disabled = false; 
          emailInput.disabled = false; 
          emailInput.style.borderColor = '#e74c3c'; 
          alert('Something went wrong.'); 
        }
      }
    });

    // Enter key submits form
    document.addEventListener('keypress', function(e) { 
      if (e.key === 'Enter' && e.target.classList.contains('variant-notify-email')) { 
        e.preventDefault(); 
        e.target.closest('.variant-notify-form').querySelector('.variant-notify-submit').click(); 
      } 
    });
  }


  /* ==========================================================================
     4. URL STATE MANAGEMENT
     ========================================================================== */

  /**
   * Save current filter state to URL for deep linking
   */
  function updateURLState() {
    const params = new URLSearchParams();
    
    // Collection (only if not default)
    if (activeCollection !== 'loose-leaf-tea-canada') {
      params.set('c', activeCollection);
    }
    
    // Tea/herbal filters
    for (const [dimension, values] of Object.entries(activeFilters)) {
      if (values && values.length > 0) {
        params.set(dimension, values.join(','));
      }
    }
    
    // Spice filters
    if (activeCollection === 'spices-herbs-seasonings') {
      for (const [dimension, value] of Object.entries(spiceFilters)) {
        if (value) {
          params.set(dimension, value);
        }
      }
    }

    // Recipe filters
    if (activeCollection === 'spice-recipes') {
      for (const [dimension, value] of Object.entries(recipeFilters)) {
        if (value) {
          params.set(dimension, value);
        }
      }
    }
    
    // Cookbook mode
    if (currentCookbookSpices) {
      params.set('cookbook', currentCookbookSpices.join(','));
    }

    // New Here? section
    if (activeCollection === 'new-here' && activeNewHereSection) {
      params.set('section', activeNewHereSection);
    }
    
    const newURL = params.toString() 
      ? `${window.location.pathname}?${params.toString()}`
      : window.location.pathname;
    window.history.replaceState({}, '', newURL);
  }

  /**
   * Restore filter state from URL on page load
   */
  function loadFiltersFromURL() {
    const params = new URLSearchParams(window.location.search);
    if (params.size === 0) return;
    
    // Get collection first
    const collection = params.get('c');
    if (collection) {
      activeCollection = collection;
      inflateCollection(collection);
      document.querySelectorAll('.category-btn').forEach(btn => {
        btn.classList.toggle('active', btn.dataset.collection === collection);
      });
      document.querySelectorAll('.sub-filters-enhanced').forEach(sf => {
        sf.classList.toggle('active', sf.dataset.parent === collection);
      });
    }

    // Restore New Here? section
    const urlSection = params.get('section');
    if (urlSection && activeCollection === 'new-here') {
      activeNewHereSection = urlSection;
      const newHereContainer = document.querySelector('.sub-filters-enhanced[data-parent="new-here"]');
      if (newHereContainer) {
        newHereContainer.querySelectorAll('.dimension-tab').forEach(tab => {
          tab.classList.toggle('active', tab.dataset.value === urlSection);
        });
      }
    }
    
    // Check for cookbook mode
    const cookbookParam = params.get('cookbook');
    if (cookbookParam) {
      setTimeout(() => {
        activateCookbookMode(cookbookParam, '');
      }, 100);
      return;
    }
    
    // Load filters
    const validFilterKeys = [
      'c', 'cookbook',
      // Tea filters
      'tea_type', 'tea_energy', 'tea_perfect_for', 'tea_character', 'tea_flavor',
      // Herbal/wellness filters
      'benefit',
      // Spice filters
      'spice_type', 'spice_protein', 'spice_dish', 'spice_cuisine',
      // Other
      'tag'
    ];

    params.forEach((value, key) => {
      // Skip unknown parameters (Shopify tracking, UTM, etc.)
      if (!validFilterKeys.includes(key)) return;
      if (key === 'c' || key === 'cookbook') return;
      
      // Spice filters
      if (key.startsWith('spice_')) {
        spiceFilters[key] = value;
        const btn = document.querySelector(`.sub-filter-btn[data-dimension="${key}"][data-value="${value}"]`);
        if (btn) {
          btn.classList.add('active');
          const cardClass = btn.dataset.cardClass;
          if (cardClass) btn.classList.add(cardClass);
        }
      } 
      // Tea/herbal filters
      else {
        const values = value.split(',');
        activeFilters[key] = values;
        values.forEach(v => {
          const btn = document.querySelector(`.sub-filter-btn[data-dimension="${key}"][data-value="${v}"]`);
          if (btn) {
            btn.classList.add('active');
            const cardClass = btn.dataset.cardClass;
            if (cardClass) btn.classList.add(cardClass);
          }
        });
      }
    });
    
    // Update UI state
    if (activeCollection === 'spices-herbs-seasonings') {
      if (typeof renderSpiceSelectedFilters === 'function') {
        renderSpiceSelectedFilters();
      }
    } else {
      renderSelectedFilters();
    }
  }

  /**
   * Announce filter results to screen readers
   */
  function announceFilterResults() {
    const visibleCount = document.querySelectorAll('.product-card:not(.hidden)').length;
    const announce = document.getElementById('filterAnnounce');
    if (announce) {
      announce.textContent = `Showing ${visibleCount} products`;
    }
  }


  /* ==========================================================================
     5. CART SYSTEM
     ========================================================================== */

  /**
   * Fetch cart from Shopify and update all displays
   */
  async function fetchCart() {
    try { 
      const response = await fetch('/cart.js'); 
      shopifyCart = await response.json(); 
      updateCartBadge(); 
      updateCartIndicators(); 
      updateQuantityDisplays();
      updateToggleQuantityDisplays();
      updateLineTotals(); 
      updateRecipeCardsForCart();
      updateOptionalButtonStates();
      updateDynamicPriceBadges();
      updateRecipeCardButtons();
      await checkAndRemoveOrphanedRecipeCards();
      return shopifyCart; 
    }
    catch (error) { 
      console.error('Error fetching cart:', error); 
      return null; 
    }
  }

  /**
   * Update cart badge count
   */
  function updateCartBadge() {
    if (!shopifyCart) return;
    const itemCount = shopifyCart.item_count || 0;
    if (itemCount > 0) {
      cartBadge.textContent = itemCount;
      cartBadge.style.display = 'inline-flex';
    } else {
      cartBadge.style.display = 'none';
    }
    updateCartDisplays();
  }

  /**
   * Update quantity displays for all variants
   */
  function updateQuantityDisplays() {
    if (!shopifyCart) return;
    const quantityMap = {};
    shopifyCart.items.forEach(item => { 
      quantityMap[item.variant_id] = item.quantity; 
    });
    document.querySelectorAll('.variant-qty-display').forEach(display => { 
      display.textContent = quantityMap[display.dataset.variantId] || 0; 
    });
  }

  /**
   * Update line totals for all variant rows
   */
  function updateLineTotals() {
    document.querySelectorAll('.variant-row-stacked').forEach(row => {
      const variantId = row.dataset.variantId;
      const variantPrice = parseInt(row.dataset.variantPrice);
      const qtyDisplay = row.querySelector(`.variant-qty-display[data-variant-id="${variantId}"]`);
      const lineTotalEl = row.querySelector(`.variant-line-total[data-variant-id="${variantId}"]`);
      if (qtyDisplay && lineTotalEl) { 
        const quantity = parseInt(qtyDisplay.textContent) || 0; 
        const lineTotal = (variantPrice * quantity) / 100; 
        lineTotalEl.textContent = `$${lineTotal.toFixed(2)}`; 
        lineTotalEl.classList.toggle('has-items', quantity > 0); 
      }
    });
  }

  /**
   * Update "In Cart" tab indicators above product cards (v4.0)
   * Sums total grams in cart from variant titles (e.g. "50g", "100g").
   * Displays as "150g in Cart" on the gold tab above the card header.
   */
  function updateCartIndicators() {
    if (!shopifyCart) return;

    // Build a handle → { totalGrams, hasNonGramItems } map
    const handleCartMap = {};
    shopifyCart.items.forEach(item => {
      if (!handleCartMap[item.handle]) {
        handleCartMap[item.handle] = { totalGrams: 0, hasNonGramItems: false, totalQty: 0 };
      }
      // Parse grams from variant title (e.g. "50g", "100g", "250g")
      const gramMatch = item.variant_title ? item.variant_title.match(/(\d+)\s*g/i) : null;
      if (gramMatch) {
        handleCartMap[item.handle].totalGrams += parseInt(gramMatch[1]) * item.quantity;
      } else {
        handleCartMap[item.handle].hasNonGramItems = true;
      }
      handleCartMap[item.handle].totalQty += item.quantity;
    });

    productCards.forEach(card => {
      if (card.classList.contains('cart-view-card')) return;
      const tab = card.querySelector('.in-cart-tab');
      if (!tab) return;

      const productHandle = card.dataset.productHandle;
      const cartData = handleCartMap[productHandle];

      if (cartData && cartData.totalQty > 0) {
        let label;
        if (cartData.totalGrams > 0) {
          label = `${cartData.totalGrams}g in Cart`;
        } else {
          // Fallback for non-gram products (teaware, gift cards, etc.)
          label = `${cartData.totalQty} in Cart`;
        }
        tab.innerHTML = `<span class="in-cart-tab-label">${label}</span>`;
        tab.classList.add('visible');
        tab.style.display = '';
      } else {
        tab.classList.remove('visible');
        tab.innerHTML = '';
      }
    });
  }

  /**
   * Show toast notification
   */
  function showToast(message) { 
    successToast.textContent = message; 
    successToast.classList.add('show'); 
    setTimeout(() => successToast.classList.remove('show'), 2000); 
  }

  /**
   * Add item to cart
   */
  async function addToCart(variantId, quantity = 1) {
    try { 
      const response = await fetch('/cart/add.js', { 
        method: 'POST', 
        headers: { 'Content-Type': 'application/json' }, 
        body: JSON.stringify({ id: variantId, quantity }) 
      }); 
      if (response.ok) { 
        await fetchCart(); 
        return true; 
      } 
      return false; 
    }
    catch (error) { 
      console.error('Error adding to cart:', error); 
      return false; 
    }
  }

  /**
   * Update cart item quantity
   */
  async function updateCartQuantity(lineKey, quantity) {
    try { 
      const response = await fetch('/cart/change.js', { 
        method: 'POST', 
        headers: { 'Content-Type': 'application/json' }, 
        body: JSON.stringify({ id: lineKey, quantity }) 
      }); 
      if (response.ok) { 
        await fetchCart(); 
        if (isCartView) await showCartView(); 
        return true; 
      } 
      return false; 
    }
    catch (error) { 
      console.error('Error updating cart:', error); 
      return false; 
    }
  }

  /**
   * Format cents as dollar string
   */
  function formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  /* ==========================================================================
    TOGGLE QUANTITY SELECTOR SYSTEM (v3.2)
  ========================================================================== */

  /**
   * Initialize toggle selectors on all product cards
   */
  function initToggleSelectors() {
    document.querySelectorAll('.variant-toggle-system').forEach(system => {
      const toggleBtns = system.querySelectorAll('.size-toggle-btn');
      
      toggleBtns.forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.stopPropagation();
          handleToggleClick(btn, system);
        });
      });
    });
  }

  /**
   * Handle toggle button click
   */
  function handleToggleClick(btn, system) {
    const toggleBtns = system.querySelectorAll('.size-toggle-btn');
    const quantityRow = system.querySelector('.variant-quantity-row');
    const card = system.closest('.product-card');
    const priceBadge = card?.querySelector('.price-badge');
    
    // Update active state on all toggles
    toggleBtns.forEach(b => b.classList.remove('active'));
    btn.classList.add('active');
    
    // Update price badge (skip if sale badge)
    if (priceBadge && !priceBadge.classList.contains('price-badge--sale')) {
      const price = parseInt(btn.dataset.variantPrice);
      priceBadge.textContent = formatMoney(price);
    }
    
    // Update quantity row content
    updateQuantityRow(quantityRow, btn, system);
  }

  /**
   * Update quantity row based on selected variant
   */
  function updateQuantityRow(quantityRow, toggleBtn, system) {
    const variantId = toggleBtn.dataset.variantId;
    const variantAvailable = toggleBtn.dataset.variantAvailable === 'true';
    const variantTitle = toggleBtn.dataset.variantTitle;
    const productHandle = toggleBtn.dataset.productHandle;
    const productId = system.dataset.productId;
    const productTitle = system.dataset.productTitle;
    
    // Get current cart quantity for this variant
    const cartItem = shopifyCart?.items?.find(item => 
      String(item.variant_id) === String(variantId)
    );
    const currentQty = cartItem ? cartItem.quantity : 0;
    
    // Update the active variant tracking
    quantityRow.dataset.activeVariant = variantId;
    
    if (variantAvailable) {
      // Show quantity controls
      quantityRow.innerHTML = `
        <div class="quantity-controls" data-variant-id="${variantId}">
          <button class="qty-btn qty-decrease" 
                  data-variant-id="${variantId}" 
                  data-product-handle="${productHandle}">−</button>
          <span class="qty-display ${currentQty > 0 ? 'has-items' : ''}" 
                data-variant-id="${variantId}">${currentQty}</span>
          <button class="qty-btn qty-increase" 
                  data-variant-id="${variantId}" 
                  data-product-title="${productTitle}" 
                  data-variant-title="${variantTitle}"
                  data-product-handle="${productHandle}">+</button>
        </div>
      `;
    } else {
      // Show Notify Me button
      quantityRow.innerHTML = `
        <div class="toggle-notify-container" data-variant-id="${variantId}">
          <button class="variant-notify-btn" 
                  data-variant-id="${variantId}" 
                  data-product-id="${productId}"
                  data-variant-title="${variantTitle}">
            Notify Me 🔔
          </button>
        </div>
      `;
    }
  }

  /**
   * Update cart indicator dots on all toggle buttons
   */
  function updateToggleCartDots() {
    const cartVariantIds = shopifyCart?.items?.map(item => String(item.variant_id)) || [];
    
    document.querySelectorAll('.size-toggle-cart-dot').forEach(dot => {
      const variantId = dot.dataset.variantId;
      dot.classList.toggle('has-items', cartVariantIds.includes(variantId));
    });
  }

  /**
   * Update quantity displays for toggle selectors
   */
  function updateToggleQuantityDisplays() {
    document.querySelectorAll('.variant-toggle-system').forEach(system => {
      const activeToggle = system.querySelector('.size-toggle-btn.active');
      if (!activeToggle) return;
      
      const variantId = activeToggle.dataset.variantId;
      const qtyDisplay = system.querySelector(`.qty-display[data-variant-id="${variantId}"]`);
      if (!qtyDisplay) return;
      
      const cartItem = shopifyCart?.items?.find(item => 
        String(item.variant_id) === String(variantId)
      );
      const qty = cartItem ? cartItem.quantity : 0;
      
      qtyDisplay.textContent = qty;
      qtyDisplay.classList.toggle('has-items', qty > 0);
    });
    
    updateToggleCartDots();
  }

  /**
   * Set active toggle to first variant in cart (for cart view)
   */
  function syncTogglesToCart() {
    if (!shopifyCart || !isCartView) return;
    
    document.querySelectorAll('.cart-view-card .variant-toggle-system').forEach(system => {
      const toggleBtns = system.querySelectorAll('.size-toggle-btn');
      let targetBtn = null;
      
      // First priority: last active variant (if still in cart)
      if (lastActiveVariantId) {
        toggleBtns.forEach(btn => {
          if (btn.dataset.variantId === String(lastActiveVariantId)) {
            const inCart = shopifyCart.items.some(item => String(item.variant_id) === String(lastActiveVariantId));
            if (inCart) targetBtn = btn;
          }
        });
      }
      
      // Fallback: first variant in cart
      if (!targetBtn) {
        toggleBtns.forEach(btn => {
          if (targetBtn) return;
          const variantId = btn.dataset.variantId;
          const inCart = shopifyCart.items.some(item => String(item.variant_id) === String(variantId));
          if (inCart) targetBtn = btn;
        });
      }
      
      if (targetBtn) {
        toggleBtns.forEach(b => b.classList.remove('active'));
        targetBtn.classList.add('active');
        
        const quantityRow = system.querySelector('.variant-quantity-row');
        if (quantityRow) {
          updateQuantityRow(quantityRow, targetBtn, system);
        }
      }
    });
  }

  /* ==========================================================================
     6. RECIPE CARD SYSTEM
     ========================================================================== */

  /* ----------------------------------
     6a. Recipe Data & Helpers
     ---------------------------------- */

  /**
   * Load recipe data from embedded JSON
   */
  function loadRecipeData() {
    const dataEl = document.getElementById('recipe-data');
    if (dataEl) {
      try {
        const parsed = JSON.parse(dataEl.textContent);
        recipeData = parsed.recipes || [];
        console.log(`Loaded ${recipeData.length} recipes`);
      } catch (e) {
        console.error('Failed to parse recipe data:', e);
      }
    }
  }

  /**
   * Get array of variant IDs currently in cart
   */
  function getCartVariantIds() {
    if (!shopifyCart || !shopifyCart.items) return [];
    return shopifyCart.items.map(item => String(item.variant_id));
  }

  /**
   * Get minimum quantity of any required spice in cart
   * Used to determine how many "sets" of a recipe are in cart
   */
  function getMinSpiceQuantity(spiceIds, cart) {
    if (!cart || !cart.items || spiceIds.length === 0) return 0;
    let minQty = Infinity;
    spiceIds.forEach(id => {
      const cartItem = cart.items.find(item => String(item.variant_id) === String(id));
      const qty = cartItem ? cartItem.quantity : 0;
      if (qty < minQty) minQty = qty;
    });
    return minQty === Infinity ? 0 : minQty;
  }

  /**
   * Check if all required spices are in cart
   */
  function allSpicesInCart(spiceIds, cartVariantIds) {
    return spiceIds.every(id => cartVariantIds.includes(String(id)));
  }

  /**
   * Get recipes that can be made with given spice IDs
   */
  function getSubsetRecipes(spiceVariantIds) {
    const spiceSet = new Set(spiceVariantIds.map(String));
    return recipeData.filter(recipe => 
      recipe.spiceIds.every(id => spiceSet.has(String(id)))
    );
  }

  /* ----------------------------------
     6b. Recipe Card State Updates
     ---------------------------------- */

  /**
   * Auto-remove recipe cards when their spices are removed
   */
  async function checkAndRemoveOrphanedRecipeCards() {
    if (!shopifyCart || !shopifyCart.items) return;
    
    const cartVariantIds = getCartVariantIds();
    const recipeCardsInCart = shopifyCart.items.filter(item => 
      item.product_type === 'Recipe Card' || 
      item.handle?.includes('recipe-card')
    );
    
    for (const recipeCard of recipeCardsInCart) {
      const recipeHandle = recipeCard.handle?.replace('-recipe-card', '');
      const recipeCardEl = document.querySelector(`[data-recipe-handle="${recipeHandle}"]`);
      
      if (recipeCardEl) {
        const addBtn = recipeCardEl.querySelector('.recipe-add-required');
        if (addBtn) {
          const spiceIds = (addBtn.dataset.spiceIds || '').split(',').filter(id => id.trim());
          const allPresent = allSpicesInCart(spiceIds, cartVariantIds);
          
          if (!allPresent) {
            console.log(`Removing orphaned recipe card: ${recipeCard.title}`);
            try {
              await fetch('/cart/change.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id: recipeCard.key, quantity: 0 })
              });
            } catch (e) {
              console.error('Failed to remove orphaned recipe card:', e);
            }
          }
        }
      }
    }
  }

  /**
   * Update all recipe cards based on cart state
   */
  function updateRecipeCardsForCart() {
    const cartVariantIds = getCartVariantIds();
    const recipeCards = document.querySelectorAll('.product-card--recipe');
    recipeCards.forEach(card => {
      updateSingleRecipeCard(card, cartVariantIds);
    });
  }

  /**
   * Update a single recipe card's button state
   */
  function updateSingleRecipeCard(card, cartVariantIds) {
    const addBtn = card.querySelector('.recipe-add-required');
    if (!addBtn) return;

    // Skip out of stock buttons - don't override their state
    if (addBtn.classList.contains('out-of-stock')) return;
    
    const spiceIdsString = addBtn.dataset.spiceIds || '';
    const spiceIds = spiceIdsString.split(',').filter(id => id.trim());
    
    const totalSpices = spiceIds.length;
    let inCartCount = 0;
    let missingIds = [];
    
    spiceIds.forEach(id => {
      if (cartVariantIds.includes(String(id))) {
        inCartCount++;
      } else {
        missingIds.push(id);
      }
    });
    
    const missingCount = totalSpices - inCartCount;
    const btnText = addBtn.querySelector('.btn-text');
    
    addBtn.classList.remove('partial-match', 'complete');
    addBtn.dataset.missingIds = missingIds.join(',');
    
    const unlockCount = addBtn.dataset.unlockCount || '1';

    if (totalSpices === 0) {
      btnText.innerHTML = '<span class="btn-label">No spices configured</span>';
    } else if (missingCount === 0) {
      // ALL IN CART - Show split button
      addBtn.classList.add('complete');
      const minQty = getMinSpiceQuantity(spiceIds, shopifyCart);
      const nextMultiple = minQty + 1;
      btnText.innerHTML = `
        <span class="recipe-btn-remove" data-action="remove">−</span>
        <span class="recipe-btn-status">${unlockCount} Recipe${parseInt(unlockCount) > 1 ? 's' : ''} Unlocked</span>
        <span class="recipe-btn-add-more" data-action="add-more">×${nextMultiple}</span>
      `;
    } else if (inCartCount > 0) {
      // PARTIAL - Some spices in cart
      addBtn.classList.add('partial-match');
      btnText.innerHTML = `<span class="btn-label">Add ${missingCount} More | Unlock ${unlockCount} Recipe${parseInt(unlockCount) > 1 ? 's' : ''}</span>`;
    } else {
      // NONE IN CART - Default state
      btnText.innerHTML = `<span class="btn-label">Add ${totalSpices} Spices | Unlock ${unlockCount} Recipe${parseInt(unlockCount) > 1 ? 's' : ''}</span>`;
    }
  }

  /**
   * Update optional spice button states (Salt/Pepper/etc.)
   */
  function updateOptionalButtonStates() {
    const cartIds = getCartVariantIds();
    
    document.querySelectorAll('.recipe-optional-btn').forEach(btn => {
      const variantId = btn.dataset.variantId;
      const isInCart = cartIds.includes(String(variantId));
      const shortLabel = btn.dataset.shortLabel;
      
      btn.classList.toggle('in-cart', isInCart);
      
      const nameEl = btn.querySelector('.optional-btn-name');
      nameEl.textContent = isInCart ? `✓ ${shortLabel}` : shortLabel;
    });
  }

  /**
   * Update dynamic price badges on recipe cards
   */
  function updateDynamicPriceBadges() {
    const cartVariantIds = getCartVariantIds();
    
    document.querySelectorAll('.product-card--recipe').forEach(card => {
      const priceBadge = card.querySelector('.price-badge');
      if (!priceBadge) return;
      
      const spiceIds = (card.dataset.spiceIds || '').split(',').filter(id => id);
      const spicePrices = (card.dataset.spicePrices || '').split(',').map(p => parseInt(p) || 0);
      
      if (spiceIds.length !== spicePrices.length) return;
      
      let remainingPrice = 0;
      let inCartCount = 0;
      
      spiceIds.forEach((id, index) => {
        if (cartVariantIds.includes(String(id))) {
          inCartCount++;
        } else {
          remainingPrice += spicePrices[index];
        }
      });
      
      if (inCartCount === spiceIds.length) {
        priceBadge.textContent = formatMoney(parseInt(card.dataset.totalPrice) || 0) + ' ✓';
        priceBadge.classList.add('price-complete');
        priceBadge.classList.remove('price-partial');
      } else if (inCartCount > 0) {
        priceBadge.textContent = '+' + formatMoney(remainingPrice);
        priceBadge.classList.add('price-partial');
        priceBadge.classList.remove('price-complete');
      } else {
        priceBadge.textContent = formatMoney(parseInt(card.dataset.totalPrice) || 0);
        priceBadge.classList.remove('price-partial', 'price-complete');
      }
    });
  }

  /**
   * Update recipe card add buttons (header buttons)
   */
  function updateRecipeCardButtons() {
    const cartVariantIds = getCartVariantIds();
    
    document.querySelectorAll('.product-card--recipe').forEach(card => {
      const addBtn = card.querySelector('.recipe-card-add-btn');
      const mainBtn = card.querySelector('.recipe-add-required');
      if (!addBtn || !mainBtn) return;
      
      const recipeVariantId = addBtn.dataset.recipeVariantId;
      const spiceIds = (mainBtn.dataset.spiceIds || '').split(',').filter(id => id);
      
      const allSpicesInCart = spiceIds.length > 0 && spiceIds.every(id => cartVariantIds.includes(String(id)));
      const cardInCart = recipeVariantId && cartVariantIds.includes(String(recipeVariantId));
      
      const textSpan = addBtn.querySelector('.recipe-card-btn-text');
      
      addBtn.classList.remove('unlocked', 'in-cart');
      
      if (!allSpicesInCart) {
        addBtn.disabled = true;
        if (textSpan) textSpan.textContent = 'Add Card';
      } else if (cardInCart) {
        addBtn.classList.add('in-cart');
        addBtn.disabled = false;
        if (textSpan) textSpan.textContent = 'Added ✓';
      } else {
        addBtn.classList.add('unlocked');
        addBtn.disabled = false;
        if (textSpan) textSpan.textContent = 'Add Card';
      }
    });
  }

  /* ----------------------------------
     6c. Recipe Cart Actions
     ---------------------------------- */

  /**
   * Remove all recipe spices + recipe card from cart
   */
  async function removeRecipeSpicesFromCart(btn) {
    const spiceIdsString = btn.dataset.spiceIds || '';
    const spiceIds = spiceIdsString.split(',').filter(id => id.trim());
    const recipeVariantId = btn.dataset.recipeVariantId;
    
    if (spiceIds.length === 0) return;
    
    btn.disabled = true;
    const btnText = btn.querySelector('.btn-text');
    const originalHTML = btnText.innerHTML;
    btnText.innerHTML = '<span class="btn-label">Removing...</span>';
    
    try {
      const updates = {};
      
      spiceIds.forEach(id => {
        const cartItem = shopifyCart?.items.find(item => String(item.variant_id) === String(id));
        if (cartItem) {
          updates[cartItem.key] = 0;
        }
      });
      
      if (recipeVariantId) {
        const recipeItem = shopifyCart?.items.find(item => String(item.variant_id) === String(recipeVariantId));
        if (recipeItem) {
          updates[recipeItem.key] = 0;
        }
      }
      
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates })
      });
      
      if (!response.ok) throw new Error('Failed to update cart');
      
      await fetchCart();
      updateRecipeCardsForCart();
      showToast(`Removed ${spiceIds.length} spices from cart`);
      
    } catch (error) {
      console.error('Remove from cart failed:', error);
      btnText.innerHTML = originalHTML;
      showToast('Failed to remove. Try again.');
    } finally {
      btn.disabled = false;
    }
  }

  /**
   * Add another complete set of recipe spices
   */
  async function addAnotherRecipeSet(btn) {
    const spiceIdsString = btn.dataset.spiceIds || '';
    const spiceIds = spiceIdsString.split(',').filter(id => id.trim());
    
    if (spiceIds.length === 0) return;
    
    btn.disabled = true;
    const btnText = btn.querySelector('.btn-text');
    const originalHTML = btnText.innerHTML;
    btnText.innerHTML = '<span class="btn-label">Adding...</span>';
    
    try {
      const items = spiceIds.map(id => ({ id: parseInt(id), quantity: 1 }));
      
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items })
      });
      
      if (!response.ok) throw new Error('Failed to add to cart');
      
      await fetchCart();
      updateRecipeCardsForCart();
      
      const newMinQty = getMinSpiceQuantity(spiceIds, shopifyCart);
      showToast(`Recipe spices ×${newMinQty} in cart`);
      
    } catch (error) {
      console.error('Add to cart failed:', error);
      btnText.innerHTML = originalHTML;
      showToast('Failed to add. Try again.');
    } finally {
      btn.disabled = false;
    }
  }

  /**
   * Add missing spices to cart
   */
  async function addRecipeSpicesToCart(btn) {
    const recipeVariantId = btn.dataset.recipeVariantId;
    let variantIds;
    const missingIds = btn.dataset.missingIds;
    
    if (btn.classList.contains('complete')) {
      return;
    }
    
    if (missingIds && missingIds.length > 0) {
      variantIds = missingIds.split(',').filter(id => id);
    } else {
      variantIds = (btn.dataset.spiceIds || '').split(',').filter(id => id);
    }
    
    if (variantIds.length === 0) return;
    
    const items = variantIds.map(id => ({ id: parseInt(id), quantity: 1 }));

    if (recipeVariantId) {
      const recipeAlreadyInCart = shopifyCart?.items.some(
        item => String(item.variant_id) === String(recipeVariantId)
      );
      if (!recipeAlreadyInCart) {
        items.push({ id: parseInt(recipeVariantId), quantity: 1 });
      }
    }
    
    btn.disabled = true;
    const btnText = btn.querySelector('.btn-text');
    btnText.innerHTML = '<span class="btn-label">Adding...</span>';
    
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items })
      });
      
      if (!response.ok) throw new Error('Failed to add to cart');
      
      await fetchCart();
      updateRecipeCardsForCart();
      showToast(`Added ${variantIds.length} spices${recipeVariantId ? ' + recipe card' : ''}!`);
      
    } catch (error) {
      console.error('Add to cart failed:', error);
      showToast('Failed to add. Try again.');
    } finally {
      btn.disabled = false;
    }
  }

  /* ----------------------------------
     6d. Cookbook Mode
     ---------------------------------- */

  /**
   * Activate cookbook mode - show recipes makeable with given spices
   */
  function activateCookbookMode(spiceIds, sourceRecipeName) {
    const spiceArray = spiceIds.split(',').filter(id => id);
    currentCookbookSpices = spiceArray;
    
    const matchingRecipes = getSubsetRecipes(spiceArray);
    const matchingHandles = matchingRecipes.map(r => r.handle);
    
    document.querySelectorAll('.product-card--recipe').forEach(card => {
      const handle = card.dataset.recipeHandle;
      if (matchingHandles.includes(handle)) {
        card.classList.add('cookbook-match');
      } else {
        card.classList.remove('cookbook-match');
      }
    });
    
    document.querySelectorAll('.product-card:not(.product-card--recipe)').forEach(card => {
      card.classList.remove('cookbook-match');
    });
    
    const grid = document.getElementById('productGrid');
    if (grid) grid.classList.add('cookbook-mode');
    
    showCookbookBanner(matchingRecipes.length, sourceRecipeName);
    updateURLState();
  }

  /**
   * Exit cookbook mode
   */
  function exitCookbookMode() {
    currentCookbookSpices = null;
    
    document.querySelectorAll('.product-card').forEach(card => {
      card.classList.remove('cookbook-match');
    });
    
    const grid = document.getElementById('productGrid');
    if (grid) grid.classList.remove('cookbook-mode');
    
    hideCookbookBanner();
    updateURLState();
    showCollection();
  }

  /**
   * Show cookbook mode banner
   */
  function showCookbookBanner(recipeCount, recipeName) {
    let banner = document.querySelector('.cookbook-header-banner');
    
    if (!banner) {
      banner = document.createElement('div');
      banner.className = 'cookbook-header-banner';
      banner.innerHTML = `
        <span class="cookbook-header-text"></span>
        <button class="cookbook-clear-btn">Clear Filter</button>
      `;
      
      const grid = document.getElementById('productGrid');
      if (grid) grid.parentNode.insertBefore(banner, grid);
      
      banner.querySelector('.cookbook-clear-btn').addEventListener('click', exitCookbookMode);
    }
    
    banner.querySelector('.cookbook-header-text').innerHTML = 
      `Showing <strong>${recipeCount} recipes</strong> you can make with these spices`;
    banner.classList.add('active');
  }

  /**
   * Hide cookbook mode banner
   */
  function hideCookbookBanner() {
    const banner = document.querySelector('.cookbook-header-banner');
    if (banner) banner.classList.remove('active');
  }

  /**
   * Update cookbook tab counts on recipe cards
   */
  function updateCookbookCounts() {
    document.querySelectorAll('.product-card--recipe').forEach(card => {
      const cookbookTab = card.querySelector('.card-tab--cookbook');
      if (!cookbookTab) return;
      
      const spiceIds = cookbookTab.dataset.spiceIds.split(',').filter(id => id);
      const subsetRecipes = getSubsetRecipes(spiceIds);
      const otherCount = Math.max(0, subsetRecipes.length - 1);
      
      const countSpan = cookbookTab.querySelector('.cookbook-count');
      if (countSpan) countSpan.textContent = otherCount;
      
      cookbookTab.style.display = otherCount === 0 ? 'none' : '';
    });
  }

  /**
   * Update unlock counts on recipe buttons
   */
  function updateUnlockCounts() {
    document.querySelectorAll('.product-card--recipe').forEach(card => {
      const addBtn = card.querySelector('.recipe-add-required');
      if (!addBtn) return;
      
      const spiceIds = (addBtn.dataset.spiceIds || '').split(',').filter(id => id);
      const subsetRecipes = getSubsetRecipes(spiceIds);
      const unlockCount = subsetRecipes.length;

      // Skip out of stock buttons
      if (addBtn.classList.contains('out-of-stock')) return;
      
      addBtn.dataset.unlockCount = unlockCount;
      
      if (!addBtn.classList.contains('complete')) {
        const btnText = addBtn.querySelector('.btn-text');
        const spiceCount = addBtn.dataset.spiceCount || spiceIds.length;
        const missingCount = spiceIds.length - (addBtn.dataset.missingIds ? addBtn.dataset.missingIds.split(',').filter(id => id).length : 0);
        
        if (addBtn.classList.contains('partial-match')) {
          btnText.innerHTML = `<span class="btn-label">Add ${missingCount} More | Unlock ${unlockCount} Recipe${unlockCount > 1 ? 's' : ''}</span>`;
        } else {
          btnText.innerHTML = `<span class="btn-label">Add ${spiceCount} Spices | Unlock ${unlockCount} Recipe${unlockCount > 1 ? 's' : ''}</span>`;
        }
      }
    });
  }

  /**
   * Populate cookbook panel with related recipes
   */
  function populateCookbookPanel(card) {
    const cookbookTab = card.querySelector('.card-tab--cookbook');
    const panel = card.querySelector('[data-panel="cookbook"]');
    if (!cookbookTab || !panel) return;
    
    const list = panel.querySelector('.related-recipes-list');
    if (!list) return;
    
    const spiceIds = cookbookTab.dataset.spiceIds.split(',').filter(id => id);
    const currentHandle = card.dataset.recipeHandle;
    const subsetRecipes = getSubsetRecipes(spiceIds).filter(r => r.handle !== currentHandle);
    
    list.innerHTML = '';
    
    subsetRecipes.forEach(recipe => {
      const li = document.createElement('li');
      li.className = 'related-recipe-item';
      li.innerHTML = `
        <span class="related-recipe-name">${recipe.name}</span>
        <span class="related-recipe-cuisine">${recipe.cuisine}</span>
      `;
      li.addEventListener('click', () => {
        const targetCard = document.querySelector(`[data-recipe-handle="${recipe.handle}"]`);
        if (targetCard) {
          targetCard.scrollIntoView({ behavior: 'smooth', block: 'center' });
          targetCard.classList.add('highlight');
          setTimeout(() => targetCard.classList.remove('highlight'), 2000);
        }
      });
      list.appendChild(li);
    });
    
    if (subsetRecipes.length === 0) {
      list.innerHTML = '<li class="no-recipes">No other recipes yet with these exact spices.</li>';
    }
  }

  /**
   * Check if recipe card matches current filters
   */
  function recipeMatchesFilters(card) {
    if (currentCookbookSpices) {
      return card.classList.contains('cookbook-match');
    }

    // Spice recipe mode: only show recipes containing the selected spice
    if (spiceRecipeMode.active) {
      const recipeSpiceIds = card.dataset.spiceIds || '';
      if (!recipeSpiceIds.includes(String(spiceRecipeMode.spiceVariantId))) {
        return false;
      }
    }

    // Check recipe filters (from standalone Recipes toggle)
    const protein = card.dataset.spiceProtein || '';
    const dish = card.dataset.spiceDish || '';
    const cuisine = card.dataset.spiceCuisine || '';

    if (recipeFilters.spice_protein &&
      !protein.toLowerCase().includes(recipeFilters.spice_protein.toLowerCase())) {
      return false;
    }

    if (recipeFilters.spice_dish &&
      !dish.toLowerCase().includes(recipeFilters.spice_dish.toLowerCase())) {
      return false;
    }

    if (recipeFilters.spice_cuisine) {
      const filterValue = recipeFilters.spice_cuisine.toLowerCase().replace(/-/g, ' ');
      const cuisineValue = cuisine.toLowerCase().replace(/-/g, ' ');
      if (!cuisineValue.includes(filterValue) && !filterValue.includes(cuisineValue)) {
        return false;
      }
    }

    return true;
  }

  /* ----------------------------------
     6e. Recipe Card Initialization
     ---------------------------------- */

  function adjustHeaderFonts() {
    // Font sizing now handled by Liquid classes (title-md, title-sm, title-xs).
    // Function kept as no-op for backward compatibility with command bar and search.
  }

  /**
   * Initialize all recipe card functionality
   */
  function initRecipeCards() {
    loadRecipeData();
    
    // Recipe button click delegation
    document.addEventListener('click', async function(e) {
      // Remove button (−)
      const removeBtn = e.target.closest('.recipe-btn-remove');
      if (removeBtn) {
        e.stopPropagation();
        const mainBtn = removeBtn.closest('.recipe-add-required');
        if (mainBtn) await removeRecipeSpicesFromCart(mainBtn);
        return;
      }
      
      // Add-more button (×N)
      const addMoreBtn = e.target.closest('.recipe-btn-add-more');
      if (addMoreBtn) {
        e.stopPropagation();
        const mainBtn = addMoreBtn.closest('.recipe-add-required');
        if (mainBtn) await addAnotherRecipeSet(mainBtn);
        return;
      }
      
      // Main button (only when not complete)
      const mainBtn = e.target.closest('.recipe-add-required');
      if (mainBtn && !e.target.closest('.recipe-btn-remove, .recipe-btn-add-more, .recipe-btn-status')) {
        e.stopPropagation();
        if (!mainBtn.classList.contains('complete')) {
          await addRecipeSpicesToCart(mainBtn);
        }
        return;
      }
      
      // Recipe card remove button in cart view
      const removeCardBtn = e.target.closest('.recipe-card-remove-btn');
      if (removeCardBtn) {
        e.stopPropagation();
        const lineKey = removeCardBtn.dataset.lineKey;
        removeCardBtn.disabled = true;
        removeCardBtn.textContent = 'Removing...';
        try {
          await updateCartQuantity(lineKey, 0);
          showToast('Recipe card removed');
          if (isCartView) await showCartView();
        } catch (error) {
          console.error('Failed to remove recipe card:', error);
          removeCardBtn.textContent = 'Remove Card';
          removeCardBtn.disabled = false;
        }
        return;
      }
    });
    
    // Cookbook tabs - trigger filter mode
    document.querySelectorAll('.card-tab--cookbook').forEach(tab => {
      tab.addEventListener('click', (e) => {
        e.stopPropagation();
        const card = tab.closest('.product-card--recipe');
        const spiceIds = tab.dataset.spiceIds;
        const recipeName = card.querySelector('.card-title')?.textContent || '';
        activateCookbookMode(spiceIds, recipeName);
      });
    });
    
    // Recipe card tab switching (Shop/Info)
    document.querySelectorAll('.product-card--recipe .card-tab').forEach(tab => {
      tab.addEventListener('click', function(e) {
        const panel = this.dataset.tab;
        if (panel === 'cookbook') return;
        
        e.stopPropagation();
        const card = this.closest('.product-card--recipe');
        
        card.querySelectorAll('.card-tab').forEach(t => t.classList.remove('active'));
        this.classList.add('active');
        
        card.querySelectorAll('.card-tab-panel').forEach(p => p.classList.remove('active'));
        const targetPanel = card.querySelector(`[data-panel="${panel}"]`);
        if (targetPanel) targetPanel.classList.add('active');

        // Set data attribute for CSS styling (replaces :has() selectors)
        card.dataset.activeTab = panel;
      });
    });
    
    // Optional spice buttons (Salt/Pepper/Garnish)
    document.querySelectorAll('.recipe-optional-btn').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        e.stopPropagation();
        const variantId = btn.dataset.variantId;
        const productTitle = btn.dataset.productTitle;
        const isInCart = btn.classList.contains('in-cart');
        
        btn.disabled = true;
        const nameEl = btn.querySelector('.optional-btn-name');
        const originalName = nameEl.textContent;
        nameEl.textContent = '...';
        
        try {
          if (isInCart) {
            const cartItem = shopifyCart?.items.find(item => String(item.variant_id) === String(variantId));
            if (cartItem) {
              await updateCartQuantity(cartItem.key, 0);
              showToast(`Removed ${productTitle}`);
            }
          } else {
            await addToCart(variantId, 1);
            showToast(`Added ${productTitle}`);
          }
        } catch (error) {
          console.error('Failed to update optional spice:', error);
          showToast('Failed. Try again.');
        } finally {
          btn.disabled = false;
          updateOptionalButtonStates();
        }
      });
    });
    
    // Initial state updates
    updateRecipeCardsForCart();
    updateOptionalButtonStates();
    updateCookbookCounts();
    updateUnlockCounts();
    updateDynamicPriceBadges();
    updateRecipeCardButtons();
    
    document.querySelectorAll('.product-card--recipe').forEach(card => {
      populateCookbookPanel(card);
    });
  }

  // Recipe card header button click handler
  document.addEventListener('click', async function(e) {
    const addBtn = e.target.closest('.recipe-card-add-btn');
    if (!addBtn || addBtn.disabled) return;
    
    e.stopPropagation();
    
    const recipeVariantId = addBtn.dataset.recipeVariantId;
    const recipeName = addBtn.dataset.recipeName;
    const isInCart = addBtn.classList.contains('in-cart');
    
    const textSpan = addBtn.querySelector('.recipe-card-btn-text');
    const originalText = textSpan ? textSpan.textContent : '';
    if (textSpan) textSpan.textContent = '...';
    addBtn.disabled = true;
    
    try {
      if (isInCart) {
        const cartItem = shopifyCart?.items.find(item => String(item.variant_id) === String(recipeVariantId));
        if (cartItem) {
          await updateCartQuantity(cartItem.key, 0);
          showToast(`Removed ${recipeName} recipe card`);
        }
      } else {
        await addToCart(recipeVariantId, 1);
        showToast(`Added ${recipeName} recipe card!`);
      }
      updateRecipeCardButtons();
    } catch (error) {
      console.error('Failed to update recipe card:', error);
      showToast('Failed. Try again.');
      if (textSpan) textSpan.textContent = originalText;
      addBtn.disabled = false;
    }
  });


  /*
  ==========================================================================
  7. SPICE & RECIPE FILTER SYSTEM (v3.3 - simplified, no tracks)
  ==========================================================================
  */

  const spiceFilterContainer = document.querySelector('.sub-filters-enhanced[data-parent="spices-herbs-seasonings"]');

  if (spiceFilterContainer) {

    /**
     * Update spice sub-filter button active states
     */
    function updateSpiceSubFilterButtonStates() {
      spiceFilterContainer.querySelectorAll('.sub-filter-btn').forEach(btn => {
        const dimension = btn.dataset.dimension;
        const value = btn.dataset.value;
        const isActive = spiceFilters[dimension] === value;
        btn.classList.toggle('active', isActive);
        if (!isActive) {
          btn.className = btn.className.replace(/\bspice-\S+/g, '').trim();
        }
      });
    }

    /**
     * Render selected spice filter pills
     */
    function renderSpiceSelectedFilters() {
      const container = spiceFilterContainer.querySelector('.selected-filters');
      if (!container) return;

      const dimensionLabels = {
        'spice_type': 'Type',
        'spice_protein': 'Protein',
        'spice_dish': 'Meals',
        'spice_cuisine': 'Cuisine'
      };

      let pillsHTML = '';
      let hasFilters = false;

      for (const [dimension, value] of Object.entries(spiceFilters)) {
        if (value) {
          hasFilters = true;
          const label = dimensionLabels[dimension] || dimension;
          const displayValue = value.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
          pillsHTML += `
            <span class="filter-pill" data-dimension="${dimension}" data-value="${value}">
              <span class="filter-pill-label">${label}:</span>
              <span class="filter-pill-value">${displayValue}</span>
              <button class="filter-pill-remove" aria-label="Remove filter">×</button>
            </span>
          `;
        }
      }

      if (hasFilters) {
        pillsHTML += `<button class="clear-all-filters">Clear All</button>`;
      }

      container.innerHTML = pillsHTML;
    }

    // Spice dimension tab clicks (v3.3 - standard tabs, no tracks)
    spiceFilterContainer.querySelectorAll('.dimension-tab').forEach(tab => {
      tab.addEventListener('click', function(e) {
        e.stopPropagation();
        const dimension = this.dataset.dimension;

        // Show options panel
        spiceFilterContainer.querySelectorAll('.dimension-options').forEach(panel => {
          panel.classList.remove('active');
        });
        const targetPanel = spiceFilterContainer.querySelector(`.dimension-options[data-for-dimension="${dimension}"]`);
        if (targetPanel) targetPanel.classList.add('active');

        // Update active tab styling
        spiceFilterContainer.querySelectorAll('.dimension-tab').forEach(t => t.classList.remove('active'));
        this.classList.add('active');
      });
    });

    // Spice sub-filter button clicks (v3.3 - mutually exclusive dimensions)
    spiceFilterContainer.querySelectorAll('.sub-filter-btn').forEach(btn => {
      btn.addEventListener('click', function(e) {
        e.stopPropagation();
        const dimension = this.dataset.dimension;
        const value = this.dataset.value;
        const cardClass = this.dataset.cardClass;
        const wasActive = this.classList.contains('active');

        // Clear ALL dimensions and ALL buttons (mutual exclusivity)
        Object.keys(spiceFilters).forEach(key => spiceFilters[key] = null);
        spiceFilterContainer.querySelectorAll('.sub-filter-btn').forEach(b => {
          b.classList.remove('active');
          b.className = b.className.replace(/\bspice-\S+/g, '').trim();
        });

        if (!wasActive) {
          // Set the clicked filter
          this.classList.add('active');
          if (cardClass) this.classList.add(cardClass);
          spiceFilters[dimension] = value;
        }
        // If wasActive, everything is already cleared (toggle off)

        updateSpiceSubFilterButtonStates();
        renderSpiceSelectedFilters();
        showCollection();
        updateURLState();
      });
    });

    // Spice pill remove clicks
    spiceFilterContainer.addEventListener('click', function(e) {
      const removeBtn = e.target.closest('.filter-pill-remove');
      if (removeBtn) {
        e.stopPropagation();
        const pill = removeBtn.closest('.filter-pill');
        const dimension = pill.dataset.dimension;
        spiceFilters[dimension] = null;

        updateSpiceSubFilterButtonStates();
        renderSpiceSelectedFilters();
        showCollection();
        updateURLState();
      }

      const clearAllBtn = e.target.closest('.clear-all-filters');
      if (clearAllBtn) {
        e.stopPropagation();
        Object.keys(spiceFilters).forEach(key => spiceFilters[key] = null);
        spiceFilterContainer.querySelectorAll('.sub-filter-btn').forEach(btn => {
          btn.classList.remove('active');
          btn.className = btn.className.replace(/\bspice-\S+/g, '').trim();
        });

        updateSpiceSubFilterButtonStates();
        renderSpiceSelectedFilters();
        showCollection();
        updateURLState();
      }
    });
  }

  /*
  --------------------------------------------------------------------------
  7b. Recipe Toggle Filter Handlers (v3.3)
  --------------------------------------------------------------------------
  */

  // Handle recipe dimension tab clicks (spice-recipes toggle)
  document.querySelectorAll('.sub-filters-enhanced[data-parent="spice-recipes"] .dimension-tab').forEach(tab => {
    tab.addEventListener('click', function() {
      const dimension = this.dataset.dimension;
      const parent = this.closest('.sub-filters-enhanced');

      // Update active tab
      parent.querySelectorAll('.dimension-tab').forEach(t => t.classList.remove('active'));
      this.classList.add('active');

      // Show corresponding options panel
      parent.querySelectorAll('.dimension-options').forEach(panel => panel.classList.remove('active'));
      const targetPanel = parent.querySelector(`.dimension-options[data-for-dimension="${dimension}"]`);
      if (targetPanel) targetPanel.classList.add('active');
    });
  });

  // Handle recipe sub-filter button clicks
  document.querySelectorAll('.sub-filters-enhanced[data-parent="spice-recipes"] .sub-filter-btn').forEach(btn => {
    btn.addEventListener('click', function() {
      const dimension = this.dataset.dimension;
      const value = this.dataset.value;

      // Toggle: if already active, deselect
      if (recipeFilters[dimension] === value) {
        recipeFilters[dimension] = null;
        this.classList.remove('active');
      } else {
        // Deselect other buttons in same dimension
        this.closest('.dimension-options').querySelectorAll('.sub-filter-btn').forEach(b => b.classList.remove('active'));
        recipeFilters[dimension] = value;
        this.classList.add('active');
      }

      renderRecipeSelectedFilters();
      showCollection();
    });
  });

  /**
   * Render selected filter pills for the Recipes toggle
   */
  function renderRecipeSelectedFilters() {
    const container = document.querySelector('.selected-filters[data-collection="spice-recipes"]');
    if (!container) return;

    container.innerHTML = '';

    const labels = {
      'spice_protein': 'Protein',
      'spice_dish': 'Dish',
      'spice_cuisine': 'Cuisine'
    };

    let hasFilters = false;

    for (const [dimension, value] of Object.entries(recipeFilters)) {
      if (!value) continue;
      hasFilters = true;

      const pill = document.createElement('span');
      pill.className = 'selected-filter-pill';
      pill.innerHTML = `${labels[dimension]}: ${value.replace(/-/g, ' ')} <button class="remove-filter" data-dimension="${dimension}" data-value="${value}">×</button>`;
      container.appendChild(pill);
    }

    if (hasFilters) {
      const clearAll = document.createElement('button');
      clearAll.className = 'clear-all-filters';
      clearAll.textContent = 'Clear All';
      clearAll.addEventListener('click', () => {
        recipeFilters.spice_protein = null;
        recipeFilters.spice_dish = null;
        recipeFilters.spice_cuisine = null;
        document.querySelectorAll('.sub-filters-enhanced[data-parent="spice-recipes"] .sub-filter-btn').forEach(b => b.classList.remove('active'));
        renderRecipeSelectedFilters();
        showCollection();
      });
      container.appendChild(clearAll);
    }

    // Handle individual pill remove
    container.querySelectorAll('.remove-filter').forEach(btn => {
      btn.addEventListener('click', function() {
        const dim = this.dataset.dimension;
        recipeFilters[dim] = null;
        document.querySelectorAll(`.sub-filter-btn[data-dimension="${dim}"]`).forEach(b => b.classList.remove('active'));
        renderRecipeSelectedFilters();
        showCollection();
      });
    });
  }

  /* ==========================================================================
    7c. SPICE CARD RECIPE TABS (v3.3)
    Adds recipe browsing inside individual spice product cards
  ========================================================================== */

  /**
   * Initialize spice recipe tab clicks on spice cards
   */
  function initSpiceRecipeTabs() {
    // Handle tab clicks on spice cards (Shop/Info/Recipes)
    document.addEventListener('click', function(e) {
      const cardTab = e.target.closest('.product-card[data-collection="spices-herbs-seasonings"] .card-tab');
      if (!cardTab) return;

      const card = cardTab.closest('.product-card');
      const panel = cardTab.dataset.tab;
      if (!panel) return;

      e.stopPropagation();

      // Update active tab
      card.querySelectorAll('.card-tab').forEach(t => t.classList.remove('active'));
      cardTab.classList.add('active');

      // Update active panel
      card.querySelectorAll('.card-tab-panel').forEach(p => p.classList.remove('active'));
      const targetPanel = card.querySelector(`.card-tab-panel[data-panel="${panel}"]`);
      if (targetPanel) targetPanel.classList.add('active');

      // Set data attribute for CSS styling (replaces :has() selectors)
      card.dataset.activeTab = panel;

      // Track tab click for Meta Pixel
      if (typeof fbq === 'function') {
        fbq('trackCustom', 'ShowroomTabClick', {
          tab_name: panel,
          product_handle: card.dataset.productHandle || '',
          collection: activeCollection
        });
      }

      // If switching to recipes tab, populate the list
      if (panel === 'recipes') {
        const variantId = card.dataset.firstVariantId;
        const spiceName = card.querySelector('.product-card-header-title')?.textContent || 'this spice';
        populateSpiceRecipesPanel(card, variantId, spiceName);
      }
    }, true);

    // Handle "Explore These Recipes" button clicks
    document.addEventListener('click', function(e) {
      const exploreBtn = e.target.closest('.spice-recipes-explore-btn');
      if (!exploreBtn) return;

      e.stopPropagation();
      e.preventDefault();

      const card = exploreBtn.closest('.product-card');
      const variantId = card.dataset.firstVariantId;
      const spiceName = card.querySelector('.product-card-header-title')?.textContent || 'this spice';

      if (variantId) {
        activateSpiceRecipeMode(variantId, spiceName);
      }
    }, true);
  }

  /**
   * Populate the recipes list inside a spice card's Recipes tab
   */
  function populateSpiceRecipesPanel(card, variantId, spiceName) {
    const panel = card.querySelector('.spice-recipes-panel');
    if (!panel) return;

    const list = panel.querySelector('.spice-recipes-list');
    const emptyState = panel.querySelector('.spice-recipes-empty');
    const exploreBtn = panel.querySelector('.spice-recipes-explore-btn');

    if (!list) return;

    // Find recipes containing this spice
    const matchingRecipes = recipeData.filter(recipe =>
      recipe.spiceIds.includes(String(variantId))
    );

    // Clear existing list
    list.innerHTML = '';

    if (matchingRecipes.length === 0) {
      list.style.display = 'none';
      if (emptyState) emptyState.style.display = 'block';
      if (exploreBtn) exploreBtn.style.display = 'none';
      return;
    }

    // Show list, hide empty state
    list.style.display = 'block';
    if (emptyState) emptyState.style.display = 'none';
    if (exploreBtn) {
      exploreBtn.style.display = 'block';
      exploreBtn.textContent = matchingRecipes.length === 1
        ? 'View This Recipe →'
        : `Explore ${matchingRecipes.length} Recipes →`;
    }

    // Populate recipe names
    matchingRecipes.forEach(recipe => {
      const li = document.createElement('li');
      li.className = 'spice-recipe-item';
      li.textContent = recipe.name;
      list.appendChild(li);
    });
  }

  /**
   * Activate spice recipe mode — switch to Recipes toggle filtered for this spice
   */
  function activateSpiceRecipeMode(variantId, spiceName) {
    // Save state
    spiceRecipeMode.active = true;
    spiceRecipeMode.previousCollection = activeCollection;
    spiceRecipeMode.spiceName = spiceName;
    spiceRecipeMode.spiceVariantId = variantId;

    // Switch to Recipes toggle
    const recipesBtn = document.querySelector('.category-btn[data-collection="spice-recipes"]');
    if (recipesBtn) {
      // Update category buttons
      document.querySelectorAll('.category-btn').forEach(b => {
        b.classList.remove('active');
        b.setAttribute('aria-pressed', 'false');
      });
      recipesBtn.classList.add('active');
      recipesBtn.setAttribute('aria-pressed', 'true');

      // Update active collection
      activeCollection = 'spice-recipes';

      // Show recipe sub-filters
      document.querySelectorAll('.sub-filters-enhanced').forEach(p => p.classList.remove('active'));
      const recipeSubFilters = document.querySelector('.sub-filters-enhanced[data-parent="spice-recipes"]');
      if (recipeSubFilters) recipeSubFilters.classList.add('active');

      // Clear any recipe filters (we're filtering by spice, not by dimension)
      recipeFilters.spice_protein = null;
      recipeFilters.spice_dish = null;
      recipeFilters.spice_cuisine = null;
      document.querySelectorAll('.sub-filters-enhanced[data-parent="spice-recipes"] .sub-filter-btn').forEach(b => b.classList.remove('active'));

      // Show the banner
      showSpiceRecipeBanner(spiceName);

      // Refresh display
      showCollection();
    }
  }

  /**
   * Exit spice recipe mode
   */
  function exitSpiceRecipeMode() {
    if (!spiceRecipeMode.active) return;

    spiceRecipeMode.active = false;
    spiceRecipeMode.spiceName = '';
    spiceRecipeMode.spiceVariantId = '';

    hideSpiceRecipeBanner();

    // Switch back to Spices
    const spicesBtn = document.querySelector('.category-btn[data-collection="spices-herbs-seasonings"]');
    if (spicesBtn) {
      spicesBtn.click();
    }
  }

  /**
   * Show the spice recipe mode banner
   */
  function showSpiceRecipeBanner(spiceName) {
    let banner = document.querySelector('.spice-recipe-banner');
    if (!banner) {
      // Create banner if it doesn't exist
      banner = document.createElement('div');
      banner.className = 'spice-recipe-banner';
      banner.innerHTML = `
        <span class="spice-recipe-banner-text"></span>
        <button class="spice-recipe-clear-btn">← Back to Spices</button>
      `;
      // Attach click handler
      banner.querySelector('.spice-recipe-clear-btn').addEventListener('click', function() {
        exitSpiceRecipeMode();
      });
      const productGrid = document.getElementById('productGrid');
      productGrid.parentNode.insertBefore(banner, productGrid);
    }

    const matchCount = recipeData.filter(r => r.spiceIds.includes(String(spiceRecipeMode.spiceVariantId))).length;
    banner.querySelector('.spice-recipe-banner-text').innerHTML =
      `Showing <strong>${matchCount} recipe${matchCount !== 1 ? 's' : ''}</strong> featuring <strong>${spiceName}</strong>`;
    banner.classList.add('active');
  }

  /**
   * Hide the spice recipe mode banner
   */
  function hideSpiceRecipeBanner() {
    const banner = document.querySelector('.spice-recipe-banner');
    if (banner) banner.classList.remove('active');
  }

  /* ==========================================================================
     8. CORE FILTER SYSTEM
     ========================================================================== */

  /* ----------------------------------
     8a. Dimension Tab Switching
     ---------------------------------- */

  document.querySelectorAll('.dimension-tab').forEach(tab => {
    tab.addEventListener('click', function() {
      // Skip spice and recipe tabs - they have their own handlers
      if (this.closest('[data-parent="spices-herbs-seasonings"]')) return;
      if (this.closest('[data-parent="spice-recipes"]')) return;
      
      const parentFilter = this.closest('.sub-filters-enhanced');
      const dimension = this.dataset.dimension;
      
      // Update active tab
      parentFilter.querySelectorAll('.dimension-tab').forEach(t => t.classList.remove('active'));
      this.classList.add('active');
      
      // Update aria-pressed
      parentFilter.querySelectorAll('.dimension-tab').forEach(t => {
        t.setAttribute('aria-pressed', t.classList.contains('active'));
      });
      
      // Show corresponding options panel
      parentFilter.querySelectorAll('.dimension-options').forEach(panel => panel.classList.remove('active'));
      const targetPanel = parentFilter.querySelector(`.dimension-options[data-for-dimension="${dimension}"]`);
      if (targetPanel) targetPanel.classList.add('active');

      // For Ingredient Guide, clear filters when switching dimensions
      const isIngredient = parentFilter.dataset.parent === 'ingredient-guide';
      if (isIngredient) {
        activeFilters = {};
        subFilterBtns.forEach(b => {
          if (b.closest('[data-parent="ingredient-guide"]')) {
            b.classList.remove('active');
          }
        });
        renderSelectedFilters();
        showCollection();
        updateURLState();
        return;
      }
      
      // For Herbal & Wellness, category tabs filter immediately
      const isHerbal = parentFilter.dataset.parent === 'herbal-wellness-loose-leaf-tea';
      if (isHerbal) {
        activeFilters = {};
        subFilterBtns.forEach(b => {
          if (b.closest('[data-parent="herbal-wellness-loose-leaf-tea"]')) {
            b.classList.remove('active');
          }
        });
        activeFilters['benefit'] = [dimension];
        renderSelectedFilters();
        showCollection();
        updateURLState();
      }
    });
  });

  /* ----------------------------------
     8b. Sub-filter Button Clicks
     ---------------------------------- */

  subFilterBtns.forEach(btn => {
    btn.addEventListener('click', function() {
      // Skip spice and recipe buttons - they have their own handlers
      if (this.closest('[data-parent="spices-herbs-seasonings"]')) return;
      if (this.closest('[data-parent="spice-recipes"]')) return;
      
      const dimension = this.dataset.dimension;
      const value = this.dataset.value;
      const cardClass = this.dataset.cardClass;
      const isHerbalWellness = activeCollection === 'herbal-wellness-loose-leaf-tea';
      
      if (isHerbalWellness) {
        // SINGLE-SELECT MODE: Only one filter at a time
        const wasActive = this.classList.contains('active');
        
        activeFilters = {};
        subFilterBtns.forEach(b => {
          if (b.closest('[data-parent="herbal-wellness-loose-leaf-tea"]')) {
            b.classList.remove('active');
          }
        });
        
        if (!wasActive) {
          this.classList.add('active');
          activeFilters[dimension] = [value];
        }
      } else {
        // SINGLE-SELECT PER DIMENSION: One value per dimension, can combine
        const wasActive = this.classList.contains('active');
        
        subFilterBtns.forEach(b => {
          if (b.dataset.dimension === dimension && b !== this) {
            b.classList.remove('active');
            b.className = b.className.replace(/\b(tea-|spice-)\S+/g, '').trim();
          }
        });
        
        if (wasActive) {
          this.classList.remove('active');
          this.className = this.className.replace(/\b(tea-|spice-)\S+/g, '').trim();
          delete activeFilters[dimension];
        } else {
          this.classList.add('active');
          if (cardClass) this.classList.add(cardClass);
          activeFilters[dimension] = [value];
        }
      }

      this.setAttribute('aria-pressed', this.classList.contains('active'));
      renderSelectedFilters();
      showCollection();
      updateURLState();
    });
  });

  /* ----------------------------------
     8c. Selected Filter Pills
     ---------------------------------- */

  /**
   * Render selected filter pills for current collection
   */
  function renderSelectedFilters() {
    document.querySelectorAll('.selected-filters').forEach(container => {
      const collection = container.dataset.collection;
      if (collection !== activeCollection) {
        container.innerHTML = '';
        return;
      }
      
      let pillsHTML = '';
      const dimensionLabels = {
        'tea_type': 'Type',
        'tea_energy': 'Energy',
        'tea_perfect_for': 'Perfect For',
        'tea_character': 'Character',
        'tea_flavor': 'Flavor',
        'spice_type': 'Type',
        'spice_protein': 'Protein',
        'spice_dish': 'Dish',
        'spice_cuisine': 'Cuisine',
        'benefit': 'Benefit',
        'ingredient_type': 'Category',
        'ingredient_benefit': 'Benefit',
        'ingredient_origin': 'Origin'
      };
      
      for (const [dimension, values] of Object.entries(activeFilters)) {
        values.forEach(value => {
          const label = dimensionLabels[dimension] || dimension;
          const displayValue = value.split('|').pop().replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
          pillsHTML += `
            <span class="filter-pill" data-dimension="${dimension}" data-value="${value}">
              <span class="filter-pill-label">${label}:</span>
              <span class="filter-pill-value">${displayValue}</span>
              <button class="filter-pill-remove" aria-label="Remove filter">×</button>
            </span>
          `;
        });
      }
      
      if (Object.keys(activeFilters).length > 1 || (Object.keys(activeFilters).length === 1 && Object.values(activeFilters)[0].length > 1)) {
        pillsHTML += `<button class="clear-all-filters">Clear All</button>`;
      }
      
      container.innerHTML = pillsHTML;
    });
  }

  // Handle pill remove clicks
  document.addEventListener('click', function(e) {
    const removeBtn = e.target.closest('.filter-pill-remove');
    if (removeBtn) {
      const pill = removeBtn.closest('.filter-pill');
      const dimension = pill.dataset.dimension;
      const value = pill.dataset.value;
      
      if (activeFilters[dimension]) {
        activeFilters[dimension] = activeFilters[dimension].filter(v => v !== value);
        if (activeFilters[dimension].length === 0) delete activeFilters[dimension];
      }
      
      const btn = document.querySelector(`.sub-filter-btn[data-dimension="${dimension}"][data-value="${value}"]`);
      if (btn) {
        btn.classList.remove('active');
        btn.className = btn.className.replace(/\b(tea-|spice-)\S+/g, '').trim();
      }

      renderSelectedFilters();
      showCollection();
      updateURLState();
    }
    
    const clearAllBtn = e.target.closest('.clear-all-filters');
    if (clearAllBtn) {
      activeFilters = {};
      subFilterBtns.forEach(btn => {
        btn.classList.remove('active');
        btn.className = btn.className.replace(/\b(tea-|spice-)\S+/g, '').trim();
      });
      renderSelectedFilters();
      showCollection();
      updateURLState();
    }
  });

  /* ----------------------------------
     8d. Collection Switching
     ---------------------------------- */

  filterBtns.forEach(btn => {
    btn.addEventListener('click', function() {
      const collection = this.dataset.collection;
      filterBtns.forEach(b => b.classList.remove('active'));
      this.classList.add('active');
      
      filterBtns.forEach(b => {
        b.setAttribute('aria-pressed', b.classList.contains('active'));
      });
      
      subFilters.forEach(sf => sf.classList.remove('active'));
      isCartView = false;
      document.querySelectorAll('.cart-view-card').forEach(card => card.remove());
      
      const relevantSubFilter = document.querySelector(`.sub-filters-enhanced[data-parent="${collection}"]`);
      if (relevantSubFilter) relevantSubFilter.classList.add('active');
      
      // Reset all filters
      activeFilters = {};
      Object.keys(spiceFilters).forEach(key => spiceFilters[key] = null);
      recipeFilters.spice_protein = null;
      recipeFilters.spice_dish = null;
      recipeFilters.spice_cuisine = null;

      // Exit spice recipe mode if active
      if (spiceRecipeMode.active) {
        spiceRecipeMode.active = false;
        spiceRecipeMode.spiceName = '';
        spiceRecipeMode.spiceVariantId = '';
        hideSpiceRecipeBanner();
      }
      
      // Exit cookbook mode if active
      if (currentCookbookSpices) {
        currentCookbookSpices = null;
        hideCookbookBanner();
        document.getElementById('productGrid')?.classList.remove('cookbook-mode');
      }
      
      if (spiceFilterContainer) {
        spiceFilterContainer.querySelectorAll('.sub-filter-btn').forEach(btn => {
          btn.classList.remove('active');
          btn.className = btn.className.replace(/\bspice-\S+/g, '').trim();
        });

        const spiceSelectedFilters = spiceFilterContainer.querySelector('.selected-filters');
        if (spiceSelectedFilters) spiceSelectedFilters.innerHTML = '';
      }

      // Clear recipe filter UI
      const recipeFilterContainer = document.querySelector('.sub-filters-enhanced[data-parent="spice-recipes"]');
      if (recipeFilterContainer) {
        recipeFilterContainer.querySelectorAll('.sub-filter-btn').forEach(btn => btn.classList.remove('active'));
        const recipeSelectedFilters = recipeFilterContainer.querySelector('.selected-filters');
        if (recipeSelectedFilters) recipeSelectedFilters.innerHTML = '';
      }
      
      subFilterBtns.forEach(b => {
        b.classList.remove('active');
        b.className = b.className.replace(/\b(tea-|spice-)\S+/g, '').trim();
      });
      
      // Reset dimension tabs to first one active
      if (relevantSubFilter) {
        relevantSubFilter.querySelectorAll('.dimension-tab').forEach((t, i) => t.classList.toggle('active', i === 0));
        relevantSubFilter.querySelectorAll('.dimension-options').forEach((p, i) => p.classList.toggle('active', i === 0));
        relevantSubFilter.querySelectorAll('.dimension-tab').forEach(t => t.classList.remove('has-selections'));
      }

      // Reset New Here? section when switching to it
      if (collection === 'new-here') {
        activeNewHereSection = 'getting-started';
      }
      
      resetDepartureState();
      activeCollection = collection;
      inflateCollection(collection);
      renderSelectedFilters();
      showCollection();
      updateURLState();
      resetPairingView();
    });
  });


  /* ==========================================================================
     9. PRODUCT MATCHING LOGIC
     ========================================================================== */

  /**
   * Check if a product card matches current filters
   */
  function productMatchesFilters(card) {
    const collection = card.dataset.collection;
    
    // Recipe cards - special handling
    if (card.classList.contains('product-card--recipe')) {
      if (currentCookbookSpices) {
        return card.classList.contains('cookbook-match');
      }
      // Show in Recipes toggle
      if (activeCollection === 'spice-recipes') {
        return recipeMatchesFilters(card);
      }
      // Show in spice recipe mode (from card Recipes tab → Explore)
      if (spiceRecipeMode.active && activeCollection === 'spice-recipes') {
        return recipeMatchesFilters(card);
      }
      return false;
    }

    // NEW HERE? COLLECTION - filter by section tab
    if (activeCollection === 'new-here') {
      if (card.dataset.collection !== 'new-here') return false;
      const cardSection = card.dataset.newhereSection;
      if (cardSection !== activeNewHereSection) return false;
      return true;
    }

    // INGREDIENT GUIDE — filter by ingredient data attributes
    if (activeCollection === 'ingredient-guide') {
      if (collection !== 'ingredient-guide') return false;
      
      const filterDimensions = Object.keys(activeFilters);
      if (filterDimensions.length === 0) return true;
      
      for (const dimension of filterDimensions) {
        const selectedValues = activeFilters[dimension];
        if (selectedValues.length === 0) continue;
        
        let cardValue = '';
        if (dimension === 'ingredient_type') cardValue = card.dataset.ingredientType || '';
        else if (dimension === 'ingredient_benefit') cardValue = card.dataset.benefitCategory || '';
        else if (dimension === 'ingredient_origin') cardValue = card.dataset.originCountry || '';
        
        const hasMatch = selectedValues.some(val => cardValue.toLowerCase().includes(val.toLowerCase()));
        if (!hasMatch) return false;
      }
      return true;
    }
    
    if (collection !== activeCollection) return false;
    
    // Hide all non-recipe cards in cookbook mode
    if (currentCookbookSpices) return false;
    
    // SPICE COLLECTION - use spiceFilters (v3.3 - four mutually exclusive dimensions)
    if (collection === 'spices-herbs-seasonings') {

      for (const [dimension, value] of Object.entries(spiceFilters)) {
        if (!value) continue;
        let cardValues = '';
        if (dimension === 'spice_type') cardValues = card.dataset.spiceType || '';
        else if (dimension === 'spice_protein') cardValues = card.dataset.spiceProtein || '';
        else if (dimension === 'spice_dish') cardValues = card.dataset.spiceDish || '';
        else if (dimension === 'spice_cuisine') cardValues = card.dataset.spiceCuisine || '';

        if (!cardValues.toLowerCase().includes(value.toLowerCase())) {
          return false;
        }
      }
      return true;
    }
    
    // ALL OTHER COLLECTIONS - use activeFilters
    const filterDimensions = Object.keys(activeFilters);
    if (filterDimensions.length === 0) return true;
    
    for (const dimension of filterDimensions) {
      const selectedValues = activeFilters[dimension];
      if (selectedValues.length === 0) continue;
      
      let cardValues = '';
      if (dimension === 'tea_type') cardValues = card.dataset.teaType || '';
      else if (dimension === 'tea_energy') cardValues = card.dataset.teaEnergy || '';
      else if (dimension === 'tea_perfect_for') cardValues = card.dataset.teaPerfectFor || '';
      else if (dimension === 'tea_character') cardValues = card.dataset.teaCharacter || '';
      else if (dimension === 'tea_flavor') cardValues = card.dataset.teaFlavor || '';
      else if (dimension === 'benefit') {
        cardValues = [
          card.dataset.benefitPrimary || '',
          card.dataset.benefitSecondary || '',
          card.dataset.benefitTertiary || ''
        ].join('|');
      }
      else if (dimension === 'tag') cardValues = card.dataset.tags || '';
      
      const hasMatch = selectedValues.some(val => {
        if (dimension === 'benefit') {
          const parts = val.split('|');
          const mainCategory = parts[0];
          const subCategory = parts.length > 1 ? parts[1] : null;
          
          if (subCategory) {
            // Full category|symptom — require exact entry match
            const primary = card.dataset.benefitPrimary || '';
            const secondary = card.dataset.benefitSecondary || '';
            const tertiary = card.dataset.benefitTertiary || '';
            return primary.toLowerCase().includes(val.toLowerCase()) ||
                  secondary.toLowerCase().includes(val.toLowerCase()) ||
                  tertiary.toLowerCase().includes(val.toLowerCase());
          }
          
          // Category-only — loose match is fine
          return cardValues.toLowerCase().includes(mainCategory.toLowerCase());
        }
        return cardValues.toLowerCase().includes(val.toLowerCase());
      });
      if (!hasMatch) return false;
    }
    return true;
  }

  /**
   * Schedule calculateFilterCounts() to run during idle time.
   * Cancels any pending calculation if called again before it runs.
   * This keeps filter clicks responsive — grid updates instantly,
   * count badges update 100-300ms later when the browser is idle.
   */
  function scheduleFilterCounts() {
    // Cancel any pending count update from a previous interaction
    if (__pendingCountUpdate) {
      if (typeof cancelIdleCallback === 'function') {
        cancelIdleCallback(__pendingCountUpdate);
      } else {
        clearTimeout(__pendingCountUpdate);
      }
      __pendingCountUpdate = null;
    }

    // Schedule the count update for when the browser is idle
    if (typeof requestIdleCallback === 'function') {
      __pendingCountUpdate = requestIdleCallback(function() {
        __pendingCountUpdate = null;
        calculateFilterCounts();
      }, { timeout: 500 });
    } else {
      // Safari fallback — requestIdleCallback not supported
      __pendingCountUpdate = setTimeout(function() {
        __pendingCountUpdate = null;
        calculateFilterCounts();
      }, 200);
    }
  }

  /**
   * Calculate and display filter counts on buttons
   */
  function calculateFilterCounts() {
    const isHerbalWellness = activeCollection === 'herbal-wellness-loose-leaf-tea';
    const isSpices = activeCollection === 'spices-herbs-seasonings';
    const isRecipes = activeCollection === 'spice-recipes';
    const isIngredientGuide = activeCollection === 'ingredient-guide';
    
    subFilterBtns.forEach(btn => {
      const parentFilter = btn.closest('.sub-filters-enhanced');
      if (!parentFilter || parentFilter.dataset.parent !== activeCollection) {
        return;
      }
      
      const dimension = btn.dataset.dimension;
      const value = btn.dataset.value;
      
      if (!btn.dataset.label) {
        btn.dataset.label = btn.textContent.trim();
      }
      
      let count = 0;

      if (isRecipes) {
        // RECIPES TOGGLE — count recipe cards (combinable filters)
        document.querySelectorAll('.product-card--recipe').forEach(card => {
          if (card.classList.contains('cart-view-card')) return;
          
          const hypotheticalFilters = {
            spice_protein: recipeFilters.spice_protein,
            spice_dish: recipeFilters.spice_dish,
            spice_cuisine: recipeFilters.spice_cuisine
          };
          hypotheticalFilters[dimension] = value;
          
          let matches = true;
          
          if (hypotheticalFilters.spice_protein) {
            const cardProtein = (card.dataset.spiceProtein || '').toLowerCase().replace(/-/g, ' ');
            const filterProtein = hypotheticalFilters.spice_protein.toLowerCase().replace(/-/g, ' ');
            if (!cardProtein.includes(filterProtein)) {
              matches = false;
            }
          }

          if (matches && hypotheticalFilters.spice_dish) {
            const cardDish = (card.dataset.spiceDish || '').toLowerCase().replace(/-/g, ' ');
            const filterDish = hypotheticalFilters.spice_dish.toLowerCase().replace(/-/g, ' ');
            if (!cardDish.includes(filterDish)) {
              matches = false;
            }
          }
          
          if (matches && hypotheticalFilters.spice_cuisine) {
            const cardCuisine = (card.dataset.spiceCuisine || '').toLowerCase().replace(/-/g, ' ');
            const filterCuisine = hypotheticalFilters.spice_cuisine.toLowerCase().replace(/-/g, ' ');
            if (!cardCuisine.includes(filterCuisine)) {
              matches = false;
            }
          }
          
          if (matches) {
            count++;
          }
        });
      } else if (isIngredientGuide) {
        // INGREDIENT GUIDE — count ingredient cards by data attributes
        const dimensionToCardAttr = {
          'ingredient_type': 'ingredientType',
          'ingredient_benefit': 'benefitCategory',
          'ingredient_origin': 'originCountry'
        };
        const ingredientCards = document.querySelectorAll('.ingredient-card');
        const cardAttr = dimensionToCardAttr[dimension];
        if (cardAttr) {
          ingredientCards.forEach(card => {
            const cardValue = (card.dataset[cardAttr] || '').toLowerCase();
            if (cardValue.includes(value.toLowerCase())) {
              count++;
            }
          });
        }
      } else {
        // ALL OTHER TOGGLES — count product cards
        productCards.forEach(card => {
          if (card.classList.contains('cart-view-card')) return;
          if (card.classList.contains('product-card--recipe')) return;
          if (card.dataset.collection !== activeCollection) return;
          
          if (isSpices) {
            // Mutually exclusive: hypothetical starts clean, only this button set
            const hypotheticalSpiceFilters = {
              spice_type: null,
              spice_protein: null,
              spice_dish: null,
              spice_cuisine: null
            };
            hypotheticalSpiceFilters[dimension] = value;
            if (productMatchesSpiceFiltersHypothetical(card, hypotheticalSpiceFilters)) {
              count++;
            }
          } else if (isHerbalWellness) {
            const cardValues = [
              card.dataset.benefitPrimary || '',
              card.dataset.benefitSecondary || '',
              card.dataset.benefitTertiary || ''
            ].join('|').toLowerCase();
            
            const parts = value.split('|');
            const mainCategory = parts[0];
            const subCategory = parts.length > 1 ? parts[1] : null;
            
            if (subCategory) {
              const primary = (card.dataset.benefitPrimary || '').toLowerCase();
              const secondary = (card.dataset.benefitSecondary || '').toLowerCase();
              const tertiary = (card.dataset.benefitTertiary || '').toLowerCase();
              if (primary.includes(value.toLowerCase()) ||
                  secondary.includes(value.toLowerCase()) ||
                  tertiary.includes(value.toLowerCase())) {
                count++;
              }
            } else {
              if (cardValues.includes(mainCategory.toLowerCase())) {
                count++;
              }
            }
          } else {
            const hypotheticalFilters = {};
            for (const [dim, vals] of Object.entries(activeFilters)) {
              if (dim !== dimension) {
                hypotheticalFilters[dim] = vals;
              }
            }
            hypotheticalFilters[dimension] = [value];
            if (productMatchesHypotheticalFilters(card, hypotheticalFilters)) {
              count++;
            }
          }
        });
      }

      const labelSpan = btn.querySelector('.sub-filter-label');
      if (labelSpan) {
        labelSpan.textContent = `${btn.dataset.label} (${count})`;
      } else {
        btn.textContent = `${btn.dataset.label} (${count})`;
      }
    });
  }

  /**
   * Helper for spice hypothetical matching
   */
  function productMatchesSpiceFiltersHypothetical(card, filters) {
    for (const [dimension, value] of Object.entries(filters)) {
      if (!value) continue;
      
      let cardValues = '';
      if (dimension === 'spice_type') cardValues = card.dataset.spiceType || '';
      else if (dimension === 'spice_protein') cardValues = card.dataset.spiceProtein || '';
      else if (dimension === 'spice_dish') cardValues = card.dataset.spiceDish || '';
      else if (dimension === 'spice_cuisine') cardValues = card.dataset.spiceCuisine || '';
      
      if (!cardValues.toLowerCase().includes(value.toLowerCase())) {
        return false;
      }
    }
    return true;
  }

  /**
   * Helper for general hypothetical filter matching
   */
  function productMatchesHypotheticalFilters(card, filters) {
    const filterDimensions = Object.keys(filters);
    if (filterDimensions.length === 0) return true;
    
    for (const dimension of filterDimensions) {
      const selectedValues = filters[dimension];
      if (selectedValues.length === 0) continue;
      
      let cardValues = '';
      if (dimension === 'tea_type') cardValues = card.dataset.teaType || '';
      else if (dimension === 'tea_energy') cardValues = card.dataset.teaEnergy || '';
      else if (dimension === 'tea_perfect_for') cardValues = card.dataset.teaPerfectFor || '';
      else if (dimension === 'tea_character') cardValues = card.dataset.teaCharacter || '';
      else if (dimension === 'tea_flavor') cardValues = card.dataset.teaFlavor || '';
      else if (dimension === 'benefit') {
        cardValues = [
          card.dataset.benefitPrimary || '',
          card.dataset.benefitSecondary || '',
          card.dataset.benefitTertiary || ''
        ].join('|');
      }
      else if (dimension === 'tag') cardValues = card.dataset.tags || '';
      
      const hasMatch = selectedValues.some(val => {
        if (dimension === 'benefit') {
          const parts = val.split('|');
          const mainCategory = parts[0];
          const subCategory = parts.length > 1 ? parts[1] : null;
          if (!cardValues.toLowerCase().includes(mainCategory.toLowerCase())) return false;
          if (subCategory) return cardValues.toLowerCase().includes(subCategory.toLowerCase());
          return true;
        }
        const valuesToCheck = val.split('|');
        return valuesToCheck.some(v => cardValues.toLowerCase().includes(v.toLowerCase()));
      });
      if (!hasMatch) return false;
    }
    return true;
  }


  /* ==========================================================================
     10. DISPLAY & UI
     ========================================================================== */

  /* ----------------------------------
     10a. Show/Hide Collections
     ---------------------------------- */

  /**
   * Show products matching current filters
   */
  /* ==========================================================================
     BENEFIT SIGNAL TABS (v3.5)
     Shows Primary/Secondary/Tertiary relevance on herbal/wellness cards
     when a category + sub-filter is active.
     ========================================================================== */

  /**
   * Update benefit signal tabs on all herbal/wellness cards.
   * Called from showCollection() after cards are shown/hidden.
   */
  function updateBenefitSignalTabs() {
    // Get all benefit signal tab elements
    const signalTabs = document.querySelectorAll('.benefit-signal-tab');
    if (signalTabs.length === 0) return;

    // Determine if we have an active benefit sub-filter (category|symptom)
    const benefitFilter = activeFilters.benefit && activeFilters.benefit.length > 0
      ? activeFilters.benefit[0]
      : null;

    // Only show tabs when a full category|symptom filter is active (contains pipe)
    const hasSubFilter = benefitFilter && benefitFilter.includes('|');

    if (!hasSubFilter) {
      // Hide all tabs
      signalTabs.forEach(tab => {
        tab.classList.remove('visible', 'tier-primary', 'tier-secondary', 'tier-tertiary');
        tab.innerHTML = '';
        tab.style.display = '';
      });
      return;
    }

    // For each herbal/wellness card, determine its tier for this filter
    document.querySelectorAll('.product-card[data-collection="herbal-wellness-loose-leaf-tea"]').forEach(card => {
      const tab = card.querySelector('.benefit-signal-tab');
      if (!tab) return;

      // Skip hidden cards
      if (card.classList.contains('hidden')) {
        tab.classList.remove('visible', 'tier-primary', 'tier-secondary', 'tier-tertiary');
        tab.innerHTML = '';
        return;
      }

      const primary = (card.dataset.benefitPrimary || '').toLowerCase();
      const secondary = (card.dataset.benefitSecondary || '').toLowerCase();
      const tertiary = (card.dataset.benefitTertiary || '').toLowerCase();
      const filterVal = benefitFilter.toLowerCase();

      // Determine highest tier match
      let tier = null;
      if (primary.includes(filterVal)) {
        tier = 'primary';
      } else if (secondary.includes(filterVal)) {
        tier = 'secondary';
      } else if (tertiary.includes(filterVal)) {
        tier = 'tertiary';
      }

      // Reset classes
      tab.classList.remove('visible', 'tier-primary', 'tier-secondary', 'tier-tertiary');

      if (tier) {
        const labels = {
          primary:   { text: 'Primary',   stars: '★★★' },
          secondary: { text: 'Secondary', stars: '★★' },
          tertiary:  { text: 'Tertiary',  stars: '★' }
        };

        tab.innerHTML = `<span class="benefit-signal-label">${labels[tier].text}</span><span class="benefit-signal-stars">${labels[tier].stars}</span>`;
        tab.classList.add('visible', 'tier-' + tier);
        tab.style.display = '';
      } else {
        tab.innerHTML = '';
      }
    });
  }

  function showCollection() {
    // DEPARTURE LOUNGE
    if (activeCollection === 'departure-lounge') {
      showDepartureLounge();
      return;
    }
    if (activeCollection === 'new-here' && !activeNewHereSection) {
      activeNewHereSection = 'getting-started';
    }
    emptyCartMessage.classList.remove('active');
    const productGrid = document.getElementById('productGrid');
    productGrid.style.minHeight = '';
    productGrid.classList.add('fading');
    
    // Inflate deferred collection if first visit
    inflateCollection(activeCollection);
    
    setTimeout(() => {
      // Phase 1: Filter and sort (no DOM writes yet)
      let visibleCount = 0;
      const visibleCards = [];
      const hiddenCards = [];
      
      const allCards = document.querySelectorAll('.product-card');
      
      allCards.forEach(card => {
        if (card.classList.contains('cart-view-card')) return;
        if (productMatchesFilters(card)) {
          visibleCards.push(card);
          visibleCount++;
        } else {
          hiddenCards.push(card);
        }
      });
      
      // Sort visible cards alphabetically
      visibleCards.sort((a, b) => {
        const titleA = (a.querySelector('.product-card-header-title') || a.querySelector('.card-title'))?.textContent.toLowerCase() || '';
        const titleB = (b.querySelector('.product-card-header-title') || b.querySelector('.card-title'))?.textContent.toLowerCase() || '';
        return titleA.localeCompare(titleB);
      });
      
      // Phase 2: Batch all DOM writes in a single rAF
      requestAnimationFrame(() => {
        // Hide non-matching cards
        hiddenCards.forEach(card => card.classList.add('hidden'));
        
        // Show and re-order matching cards via fragment (single reflow)
        const fragment = document.createDocumentFragment();
        visibleCards.forEach(card => {
          card.classList.remove('hidden');
          fragment.appendChild(card);
        });
        productGrid.appendChild(fragment);
        
        noResults.classList.toggle('active', visibleCount === 0 && !isCartView);
        productGrid.classList.remove('fading');
        productGrid.classList.remove('showroom-loading');
        
        initializeCarousels();
        scheduleFilterCounts();
        announceFilterResults();
        updateBenefitSignalTabs();
      });
    }, 150);
  }

  /**
   * Show cart view with products in cart
   */
  async function showCartView() {
      emptyCartMessage.classList.remove('active'); 
      noResults.classList.remove('active');
      productCards.forEach(card => { 
        if (!card.classList.contains('cart-view-card')) card.classList.add('hidden'); 
      });
      
      const cart = await fetchCart();
      if (!cart || cart.items.length === 0) { 
        document.querySelectorAll('.cart-view-card').forEach(card => card.remove());
        emptyCartMessage.classList.add('active');
        document.getElementById('productGrid').style.minHeight = '0';
        return; 
      }
      
      const productGroups = {};
      cart.items.forEach(item => { 
        if (!productGroups[item.handle]) productGroups[item.handle] = { product: item, variants: [] }; 
        productGroups[item.handle].variants.push(item); 
      });
      
      const productGrid = document.getElementById('productGrid');
      
      // Build new cards into a fragment BEFORE removing old ones
      const fragment = document.createDocumentFragment();
      
      Object.keys(productGroups).forEach(handle => {
        const group = productGroups[handle];
        let originalCard = null;
        productCards.forEach(card => { 
          if (card.dataset.productHandle === handle && !card.classList.contains('cart-view-card')) originalCard = card; 
        });
        
        if (!originalCard) {
          const simpleCard = document.createElement('div');
          simpleCard.className = 'product-card cart-view-card recipe-card-in-cart';
          simpleCard.dataset.variantId = group.product.variant_id;
          simpleCard.dataset.lineKey = group.product.key;
          simpleCard.innerHTML = `
            <div class="product-card-header recipe-card-header">
              <span class="product-card-header-title">${group.product.product_title}</span>
            </div>
            <div class="product-image-wrapper">
              <div class="product-carousel">
                <div class="carousel-track">
                  <img src="${group.product.image || ''}" 
                      alt="${group.product.product_title}" 
                      class="product-image" loading="lazy">
                </div>
                <div class="price-badge">FREE</div>
              </div>
            </div>
            <div class="product-info">
              <div class="product-info-content">
                <p class="recipe-card-note">Recipe card included with your spices</p>
                <button class="recipe-card-remove-btn" data-line-key="${group.product.key}">
                  Remove Card
                </button>
              </div>
            </div>
          `;
          fragment.appendChild(simpleCard);
          return;
        }
        
        const cartCard = originalCard.cloneNode(true);
        cartCard.classList.add('cart-view-card');
        cartCard.classList.remove('hidden');
        fragment.appendChild(cartCard);
      });
      
      // Single swap: remove old, add new — no empty frame
      document.querySelectorAll('.cart-view-card').forEach(card => card.remove());
      
      // Suppress hover transition on new cards so they don't "pop" under cursor
      fragment.querySelectorAll('.cart-view-card').forEach(card => {
        card.style.transition = 'none';
      });
      productGrid.appendChild(fragment);
      
      // Restore transitions after browser has painted the settled state
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          document.querySelectorAll('.cart-view-card').forEach(card => {
            card.style.transition = '';
          });
        });
      });
      
      initializeCarousels();
      initToggleSelectors();
      syncTogglesToCart();
    }

  /* ----------------------------------
     10b. Carousels
     ---------------------------------- */

  /**
   * Initialize image carousels on product cards
   */
  function initializeCarousels() {
    document.querySelectorAll('.product-carousel').forEach(carousel => {
      if (__initializedCarousels.has(carousel)) return;
      __initializedCarousels.add(carousel);

      const track = carousel.querySelector('.carousel-track');
      const prevBtn = carousel.querySelector('.carousel-prev');
      const nextBtn = carousel.querySelector('.carousel-next');
      const dots = carousel.querySelectorAll('.carousel-dot');
      const images = carousel.querySelectorAll('.product-image');
      
      if (images.length <= 1) return;
      
      let currentIndex = 0;
      
      function updateCarousel() { 
        track.style.transform = `translateX(-${currentIndex * 100}%)`; 
        dots.forEach((dot, index) => dot.classList.toggle('active', index === currentIndex)); 
      }
      
      prevBtn?.addEventListener('click', (e) => { 
        e.stopPropagation(); 
        currentIndex = (currentIndex - 1 + images.length) % images.length; 
        updateCarousel(); 
      });
      
      nextBtn?.addEventListener('click', (e) => { 
        e.stopPropagation(); 
        currentIndex = (currentIndex + 1) % images.length; 
        updateCarousel(); 
      });
      
      dots.forEach((dot, index) => { 
        dot.addEventListener('click', (e) => { 
          e.stopPropagation(); 
          currentIndex = index; 
          updateCarousel(); 
        }); 
      });
    });
  }

  /* ----------------------------------
     10c. Scroll & Navigation
     ---------------------------------- */

  // Category scroll arrows
  const categoryScrollContainer = document.getElementById('mainCategories');
  const categoryScrollPrev = document.getElementById('categoryScrollPrev');
  const categoryScrollNext = document.getElementById('categoryScrollNext');
  const categoriesWrapper = document.getElementById('categoriesWrapper');

  function updateScrollArrows() { 
    const { scrollWidth, clientWidth } = categoryScrollContainer; 
    categoriesWrapper.classList.toggle('no-scroll', scrollWidth <= clientWidth); 
  }

  categoryScrollPrev.addEventListener('click', () => categoryScrollContainer.scrollBy({ left: -200, behavior: 'smooth' }));
  categoryScrollNext.addEventListener('click', () => categoryScrollContainer.scrollBy({ left: 200, behavior: 'smooth' }));
  window.addEventListener('load', updateScrollArrows);
  window.addEventListener('resize', updateScrollArrows);
  categoryScrollContainer.addEventListener('scroll', updateScrollArrows);

  // Back to Top Button
  const backToTopBtn = document.querySelector('.back-to-top');
  if (backToTopBtn) {
    let _scrollTicking = false;
    window.addEventListener('scroll', function() {
      if (!_scrollTicking) {
        _scrollTicking = true;
        requestAnimationFrame(function() {
          if (window.scrollY > 400) {
            backToTopBtn.classList.add('visible');
          } else {
            backToTopBtn.classList.remove('visible');
          }
          _scrollTicking = false;
        });
      }
    }, { passive: true });

    backToTopBtn.addEventListener('click', function() {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    });
  }

  /* ----------------------------------
     10d. Card Tab Switching
     ---------------------------------- */

  // Non-recipe card tab switching
  document.addEventListener('click', function(e) {
      const tab = e.target.closest('.card-tab');
      if (!tab) return;
      
      // Skip recipe cards - they have their own handler
      if (tab.closest('.product-card--recipe')) return;
      
      e.stopPropagation();
      const card = tab.closest('.product-card');
      const targetPanel = tab.dataset.tab;
      
      card.querySelectorAll('.card-tab').forEach(t => t.classList.remove('active'));
      tab.classList.add('active');
      
      card.querySelectorAll('.card-tab-panel').forEach(p => p.classList.remove('active'));
      const panel = card.querySelector(`.card-tab-panel[data-panel="${targetPanel}"]`);
      if (panel) panel.classList.add('active');

      // Set data attribute for CSS styling (replaces :has() selectors)
      card.dataset.activeTab = targetPanel;

      // Track tab click for Meta Pixel
      if (typeof fbq === 'function') {
        fbq('trackCustom', 'ShowroomTabClick', {
          tab_name: targetPanel,
          product_handle: card.dataset.productHandle || '',
          collection: activeCollection
        });
      }
  });

  /* ==========================================================================
     11. INITIALIZATION
     ========================================================================== */

  /* ----------------------------------
     11a. Quantity Button Handlers
     ---------------------------------- */

  document.addEventListener('click', async function(e) {
    if (isProcessing) return;
    
    // NEW TOGGLE SYSTEM: .qty-btn buttons
    const toggleIncreaseBtn = e.target.closest('.qty-btn.qty-increase');
    if (toggleIncreaseBtn && !toggleIncreaseBtn.disabled) {
      e.stopPropagation();
      isProcessing = true;
      toggleIncreaseBtn.style.opacity = '0.5';
      
      const variantId = toggleIncreaseBtn.dataset.variantId;
      lastActiveVariantId = variantId;
      const productTitle = toggleIncreaseBtn.dataset.productTitle;
      const variantTitle = toggleIncreaseBtn.dataset.variantTitle;
      const cartItem = shopifyCart?.items.find(item => String(item.variant_id) === String(variantId));
      
      showToast(cartItem ? `Adding another ${variantTitle}...` : `Adding ${productTitle}...`);
      
      if (cartItem) {
        await updateCartQuantity(cartItem.key, cartItem.quantity + 1);
      } else {
        await addToCart(variantId, 1);
      }
      
      toggleIncreaseBtn.style.opacity = '1';
      isProcessing = false;
      return;
    }
    
    const toggleDecreaseBtn = e.target.closest('.qty-btn.qty-decrease');
    if (toggleDecreaseBtn && !toggleDecreaseBtn.disabled) {
      e.stopPropagation();
      isProcessing = true;
      toggleDecreaseBtn.style.opacity = '0.5';
      
      const variantId = toggleDecreaseBtn.dataset.variantId;
      lastActiveVariantId = variantId;
      const cartItem = shopifyCart?.items.find(item => String(item.variant_id) === String(variantId));
      
      if (cartItem && cartItem.quantity > 0) {
        showToast(cartItem.quantity === 1 ? `Removing...` : `Updating...`);
        await updateCartQuantity(cartItem.key, cartItem.quantity - 1);
      }
      
      toggleDecreaseBtn.style.opacity = '1';
      isProcessing = false;
      return;
    }
    
    // LEGACY STACKED SYSTEM: .qty-increase / .qty-decrease (not .qty-btn)
    const increaseBtn = e.target.closest('.qty-increase:not(.qty-btn)');
    if (increaseBtn && !increaseBtn.disabled) {
      e.stopPropagation(); 
      isProcessing = true; 
      increaseBtn.style.opacity = '0.5';
      
      const variantId = increaseBtn.dataset.variantId;
      const productTitle = increaseBtn.dataset.productTitle;
      const variantTitle = increaseBtn.dataset.variantTitle;
      const cartItem = shopifyCart?.items.find(item => String(item.variant_id) === String(variantId));
      
      showToast(cartItem ? `Adding another ${variantTitle}...` : `Adding ${productTitle}...`);
      
      if (cartItem) { 
        await updateCartQuantity(cartItem.key, cartItem.quantity + 1); 
      } else { 
        await addToCart(variantId, 1); 
      }
      
      increaseBtn.style.opacity = '1'; 
      isProcessing = false;
    }
    
    const decreaseBtn = e.target.closest('.qty-decrease:not(.qty-btn)');
    if (decreaseBtn) {
      e.stopPropagation(); 
      isProcessing = true; 
      decreaseBtn.style.opacity = '0.5';
      
      const variantId = decreaseBtn.dataset.variantId;
      const cartItem = shopifyCart?.items.find(item => String(item.variant_id) === String(variantId));
      
      if (cartItem && cartItem.quantity > 0) {
        showToast(cartItem.quantity === 1 ? `Removing...` : `Updating...`);
        await updateCartQuantity(cartItem.key, cartItem.quantity - 1);
      }
      
      decreaseBtn.style.opacity = '1'; 
      isProcessing = false;
    }
  });

  /* ----------------------------------
     11b. Product Card Click Handler
     ---------------------------------- */

  document.addEventListener('click', function(e) {
    // Ignore clicks on interactive elements
    if (e.target.closest('.variant-qty-btn, .qty-increase, .qty-decrease, .qty-btn, .size-toggle-btn, .variant-toggle-system, .variant-size-toggle, .variant-quantity-row, .variant-controls, .carousel-arrow, .carousel-dot, .variant-notify-btn, .variant-notify-form, .variant-notify-email, .variant-notify-submit, .variant-notify-container, .select-zone-btn, .product-card--shipping-zone, .card-tab, .card-tabs, .in-cart-tab, .benefit-signal-tab, .recipe-add-required, .product-card--recipe .spice-item, .recipe-btn-remove, .recipe-btn-add-more, .recipe-btn-status, .recipe-card-remove-btn')) return;
    
    const card = e.target.closest('.product-card');
    if (card) { 
      const urlToOpen = card.dataset.externalUrl || card.dataset.productUrl; 
      if (urlToOpen) window.open(urlToOpen, '_blank'); 
    }
  });

  /* ----------------------------------
     11c. View Cart Button
     ---------------------------------- */

  document.getElementById('viewCartBtn').addEventListener('click', function() { 
    isCartView = true; 
    showCartView(); 
    filterBtns.forEach(b => b.classList.remove('active')); 
    this.classList.add('active'); 
    subFilters.forEach(sf => sf.classList.remove('active')); 
  });

  /* ----------------------------------
     11d. Shipping Zone Handlers
     ---------------------------------- */

  // Shipping zone card selection
  document.addEventListener('click', function(e) {
    const selectBtn = e.target.closest('.select-zone-btn');
    if (selectBtn) {
      e.stopPropagation();
      selectShippingZone(selectBtn.dataset.zoneHandle);
      return;
    }
    
    const zoneCard = e.target.closest('.product-card--shipping-zone');
    if (zoneCard) {
      e.stopPropagation();
      selectShippingZone(zoneCard.dataset.zoneHandle);
    }
  });

  // Shipping button as collection toggle
  document.getElementById('shippingSelector')?.addEventListener('click', function() {
    filterBtns.forEach(b => b.classList.remove('active'));
    this.classList.add('active');
    subFilters.forEach(sf => sf.classList.remove('active'));
    isCartView = false;
    activeCollection = 'shipping-zones';
    activeFilters = {};
    showCollection();
  });

  // Handle "View These Blends in Shop" button on ingredient cards
  document.addEventListener('click', function(e) {
    var viewBtn = e.target.closest('.view-teas-btn');
    if (!viewBtn) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    // Collect product handles from the tea list items in this card
    var card = viewBtn.closest('.ingredient-card');
    var linkedHandles = [];
    if (card) {
      card.querySelectorAll('.tea-list-item[data-product-handle]').forEach(function(item) {
        linkedHandles.push(item.dataset.productHandle);
      });
    }
    
    // Switch to target collection
    var targetCollection = viewBtn.dataset.collectionTarget || 'herbal-wellness-loose-leaf-tea';
    var categoryBtn = document.querySelector('.category-btn[data-collection="' + targetCollection + '"]');
    if (categoryBtn) {
      categoryBtn.click();
      
      if (linkedHandles.length > 0) {
        setTimeout(function() {
          // Hide products not linked to this ingredient
          document.querySelectorAll('.product-card[data-collection="' + targetCollection + '"]').forEach(function(prodCard) {
            var handle = prodCard.dataset.productHandle || '';
            if (!linkedHandles.includes(handle)) {
              prodCard.classList.add('hidden');
            }
          });
          
          // Show banner
          var ingredientName = viewBtn.dataset.ingredientName || 'this ingredient';
          var grid = document.getElementById('productGrid');
          var existingBanner = grid.querySelector('.ingredient-filter-banner');
          if (existingBanner) existingBanner.remove();
          
          var banner = document.createElement('div');
          banner.className = 'ingredient-filter-banner active';
          banner.innerHTML = '<span class="ingredient-filter-banner-text">Showing blends containing <strong>' + ingredientName + '</strong> <span class="ingredient-filter-count">(' + linkedHandles.length + ')</span></span>' +
            '<button class="ingredient-filter-clear-btn">Show All Wellness</button>';
          grid.parentNode.insertBefore(banner, grid);
          
          banner.querySelector('.ingredient-filter-clear-btn').addEventListener('click', function() {
            banner.remove();
            showCollection();
          });
          
          grid.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 250);
      } else {
        setTimeout(function() {
          document.getElementById('productGrid').scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 200);
      }
    }
  }, true);

  // Handle "Learn About These Ingredients" button on herbal/wellness cards
  document.addEventListener('click', function(e) {
    var btn = e.target.closest('.view-ingredients-btn');
    if (!btn) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    var ingredientsText = btn.dataset.ingredients || '';
    var productTitle = btn.dataset.productTitle || 'this tea';
    
    if (!ingredientsText) return;
    
    // Parse ingredient names from comma-separated list, strip "Organic"
    var ingredientNames = ingredientsText
      .split(',')
      .map(function(s) { return s.trim().toLowerCase().replace(/^organic\s+/i, ''); })
      .filter(function(s) { return s.length > 0; });
    
    // Switch to Ingredient Guide
    var ingredientBtn = document.querySelector('.category-btn[data-collection="ingredient-guide"]');
    if (ingredientBtn) {
      ingredientBtn.click();
      
      setTimeout(function() {
        // Filter ingredient cards to only show matches
        var cards = document.querySelectorAll('.ingredient-card');
        var visibleCount = 0;
        
        cards.forEach(function(card) {
          var cardName = (card.querySelector('.product-card-header-title') || 
                         card.querySelector('.card-title') || 
                         { textContent: '' }).textContent.toLowerCase();
          
          var matches = ingredientNames.some(function(name) {
            return cardName.includes(name) || name.includes(cardName);
          });
          
          if (matches) {
            card.classList.remove('hidden');
            visibleCount++;
          } else {
            card.classList.add('hidden');
          }
        });
        
        // Show banner above grid
        var grid = document.getElementById('productGrid');
        var existingBanner = document.querySelector('.product-ingredients-filter-banner');
        if (existingBanner) existingBanner.remove();
        
        var banner = document.createElement('div');
        banner.className = 'product-ingredients-filter-banner active';
        banner.innerHTML = '<span class="product-ingredients-filter-banner-text">' +
          'Ingredients in <strong>' + productTitle + '</strong> ' +
          '<span class="product-ingredients-filter-count">(' + visibleCount + ' ingredient' + (visibleCount !== 1 ? 's' : '') + ')</span></span>' +
          '<button class="product-ingredients-filter-clear-btn">Show All Ingredients</button>';
        
        grid.parentNode.insertBefore(banner, grid);
        
        banner.querySelector('.product-ingredients-filter-clear-btn').addEventListener('click', function() {
          banner.remove();
          showCollection();
        });
        
        grid.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 250);
    }
  }, true);

  /**
   * Handle New Here? section tab clicks
   */
  function initNewHereTabs() {
    const newHereContainer = document.querySelector('.sub-filters-enhanced[data-parent="new-here"]');
    if (!newHereContainer) return;

    const tabs = newHereContainer.querySelectorAll('.dimension-tab');
    
    tabs.forEach(tab => {
      tab.addEventListener('click', function(e) {
        e.stopPropagation();
        
        // Update active tab styling
        tabs.forEach(t => t.classList.remove('active'));
        this.classList.add('active');
        
        // Update active section
        activeNewHereSection = this.dataset.value;
        
        // Refresh display
        showCollection();
      });
    });
  }

  // Navigate to specific tea type from New Here? intro cards
  function navigateToTeaType(teaType) {
    activeCollection = 'loose-leaf-tea-canada';
    activeFilters = { tea_type: [teaType] };
    
    filterBtns.forEach(btn => {
      btn.classList.toggle('active', btn.dataset.collection === 'loose-leaf-tea-canada');
    });
    
    subFilters.forEach(sf => {
      sf.classList.toggle('active', sf.dataset.parent === 'loose-leaf-tea-canada');
    });
    
    const teaSubFilter = document.querySelector('.sub-filters-enhanced[data-parent="loose-leaf-tea-canada"]');
    if (teaSubFilter) {
      teaSubFilter.querySelectorAll('.dimension-tab').forEach(tab => {
        tab.classList.toggle('active', tab.dataset.dimension === 'tea_type');
      });
      teaSubFilter.querySelectorAll('.dimension-options').forEach(panel => {
        panel.classList.toggle('active', panel.dataset.forDimension === 'tea_type');
      });
    }
    
    // Activate the matching type button
    const typeBtn = document.querySelector(`.sub-filter-btn[data-dimension="tea_type"][data-value="${teaType}"]`);
    if (typeBtn) {
      typeBtn.classList.add('active');
    }
    
    const newHereSubFilter = document.querySelector('.sub-filters-enhanced[data-parent="new-here"]');
    if (newHereSubFilter) {
      newHereSubFilter.classList.remove('active');
    }
    
    renderSelectedFilters();
    showCollection();
    updateURLState();
  }

  document.addEventListener('click', function(e) {
    const viewTeasBtn = e.target.closest('.btn-view-teas');
    if (viewTeasBtn) {
      const teaType = viewTeasBtn.dataset.teaType;
      if (teaType) {
        if (teaType === 'herbal' || teaType === 'wellness') {
          navigateToHerbalWellness();
        } else {
          navigateToTeaType(teaType);
        }
      }
      return;
    }
  });

  // Navigate to Herbal & Wellness collection from New Here?
  function navigateToHerbalWellness() {
    activeCollection = 'herbal-wellness-loose-leaf-tea';
    inflateCollection('herbal-wellness-loose-leaf-tea');
    activeFilters = {};
    
    filterBtns.forEach(btn => {
      btn.classList.toggle('active', btn.dataset.collection === 'herbal-wellness-loose-leaf-tea');
    });
    
    subFilters.forEach(sf => {
      sf.classList.toggle('active', sf.dataset.parent === 'herbal-wellness-loose-leaf-tea');
    });
    
    const newHereSubFilter = document.querySelector('.sub-filters-enhanced[data-parent="new-here"]');
    if (newHereSubFilter) {
      newHereSubFilter.classList.remove('active');
    }
    
    renderSelectedFilters();
    showCollection();
    updateURLState();
  }

  // Expose functions needed by standalone scripts (departure lounge, pairing view)
  window.showToast = showToast;
  window.fetchCart = fetchCart;
  window.initializeCarousels = initializeCarousels;
  window.announceFilterResults = announceFilterResults;

  /* ----------------------------------
     11e. Startup Sequence
     ---------------------------------- */

  // Set initial active tab attribute on all cards (replaces :has() selectors)
  document.querySelectorAll('.product-card').forEach(function(card) {
    var activePanel = card.querySelector('.card-tab-panel.active');
    if (activePanel && activePanel.dataset.panel) {
      card.dataset.activeTab = activePanel.dataset.panel;
    }
  });

  // Initialize all systems
  initializeInlineNotify();
  initializeShippingZone();
  initToggleSelectors();
  fetchCart();
  loadFiltersFromURL();
  showCollection();
  initializeCarousels();
  initRecipeCards();
  initSpiceRecipeTabs();
  initNewHereTabs();

})();